// import react and react hooks to use different react features from components
import React, { useState, useEffect, useRef, useCallback } from 'react';
// import MUI components
import { Button, Container, Typography, Box, Card, CardContent, Avatar, IconButton, TextField, CardMedia,
    Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider, 
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Link,
  Grid, CircularProgress, Skeleton
 } from '@mui/material';
// import UserPost component where post writing lives
import UserPosts from '../components/UserPosts/user_posts';
import axios from 'axios';
import { GoogleGenAI } from "@google/genai";
import { useHistory } from 'react-router-dom';
import LexicalBlogEditor from "../components/LexicalBlogEditor";

import { $generateHtmlFromNodes } from "@lexical/html";
import { createEditor } from "lexical";
import DOMPurify from "dompurify";
import { LinkNode, AutoLinkNode } from "@lexical/link";
import { ListNode, ListItemNode } from "@lexical/list";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { CodeNode, CodeHighlightNode } from "@lexical/code";
import { HorizontalRuleNode } from "@lexical/extension";
import { ImageNode } from "../components/ImageNode";
import logo from "../components/Logo/Colloquia_Logo.png";
import Tooltip from '@mui/material/Tooltip';

function useInfiniteList(endpoint) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadingRef = useRef(false); // prevents race conditions

  const reset = useCallback(() => {
    setItems([]);
    setPage(1);
    setHasMore(true);
    setError(null);
    loadingRef.current = false;
  }, []);

  const loadMore = useCallback(async (pageOverride) => {
    const isExplicitReset = pageOverride === 1;
    
    // Only bail if it's NOT an explicit reset trigger
    if (!isExplicitReset && (loadingRef.current || !hasMore)) return;
    
    // For explicit resets, force-clear the lock
    if (isExplicitReset) {
        loadingRef.current = false;
    }

    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    const currentPage = pageOverride ?? page;
    try {
        const res = await axios.get(endpoint, {
            params: { page: currentPage, limit: 15 }
        });
        const key = endpoint.includes('followers') ? 'followers' : 'following';
        const newItems = res.data[key] || [];
        const pagination = res.data.pagination;

        setItems(prev => currentPage === 1 ? newItems : [...prev, ...newItems]);
        setHasMore(pagination.hasMore);
        setPage(currentPage + 1);
    } catch (err) {
        setError(err);
        console.error(`Error fetching ${endpoint}:`, err);
    } finally {
        setLoading(false);
        loadingRef.current = false;
    }
    }, [endpoint, page, hasMore]);

  // Temporary debug
  //console.log(`[useInfiniteList ${endpoint}] items=${items.length} page=${page} hasMore=${hasMore} loading=${loading}`);

  return { items, hasMore, loading, error, loadMore, reset };
}

function UserRowSkeleton() {
  return (
    <ListItem sx={{ py: 1.2, px: 1.5 }}>
      <ListItemAvatar>
        <Skeleton variant="circular" width={40} height={40} />
      </ListItemAvatar>
      <Box sx={{ flex: 1 }}>
        <Skeleton width="40%" height={20} />
        <Skeleton width="25%" height={16} />
      </Box>
      <Skeleton variant="rounded" width={90} height={30} />
    </ListItem>
  );
}

function InfiniteUserList({
  items,
  hasMore,
  loading,
  onLoadMore,
  scrollRef,
  currentUserId,
  followingMap,
  hoveredButton,
  onHoverEnter,
  onHoverLeave,
  onFollowToggle,
  onVisitProfile,
  emptyLabel = "No users yet."
}) {
  const observerRef = useRef(null);

  // Callback ref — fires every time the sentinel mounts/unmounts
  const sentinelRef = useCallback((node) => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node || !hasMore) return;

    observerRef.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onLoadMore();
    }, {
      root: scrollRef?.current || null,
      rootMargin: '100px',
      threshold: 0
    });

    observerRef.current.observe(node);
  }, [hasMore, onLoadMore, scrollRef]);

  if (!loading && items.length === 0) {
    return (
      <Typography align="center" color="text.secondary" sx={{ mt: 3 }}>
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <List disablePadding>
      {items.map((u) => {
        //const isFollowing = !!followingMap[u._id];
        const isFollowing = !!followingMap[u._id?.toString()];
        const isHovered = hoveredButton === u._id;
        return (
          <ListItem
            key={u._id}
            onClick={(e) => onVisitProfile(e, u._id)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.2, px: 1.5,
              borderRadius: 2,
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              "&:hover": { backgroundColor: "#f5f5f5" }
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ListItemAvatar>
                <Avatar src={u.profileImageUrl || "/default-avatar.jpg"} />
              </ListItemAvatar>
              <ListItemText
                primary={`${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unknown"}
                secondary={`@${u.username}`}
              />
            </Box>

            {u._id !== currentUserId && (
              <Button
                size="small"
                variant={isFollowing ? "contained" : "outlined"}
                onClick={(e) => onFollowToggle(e, u._id)}
                onMouseEnter={() => onHoverEnter(u._id)}
                onMouseLeave={onHoverLeave}
                sx={{
                  minWidth: "100px",
                  textTransform: "none",
                  transition: "all 0.2s ease-in-out",
                  backgroundColor: isFollowing && isHovered ? "error.main" : isFollowing ? "primary.main" : "transparent",
                  color: isFollowing ? "#fff" : "primary.main",
                  borderColor: "primary.main",
                  "&:hover": {
                    backgroundColor: isFollowing && isHovered ? "error.dark" : "primary.light"
                  }
                }}
              >
                {isFollowing ? (isHovered ? "Unfollow" : "Following") : "Follow"}
              </Button>
            )}
          </ListItem>
        );
      })}

      {loading && Array.from({ length: 4 }).map((_, i) => (
        <UserRowSkeleton key={`skeleton-${i}`} />
      ))}

      {/* Always rendered when hasMore — no !loading condition */}
      {hasMore && (
        <Box ref={sentinelRef} sx={{ height: '10px', width: '100%' }} aria-hidden />
      )}

      {!hasMore && items.length > 0 && (
        <Typography
          align="center"
          variant="caption"
          color="text.disabled"
          sx={{ display: 'block', py: 1.5 }}
        >
          You've reached the end
        </Typography>
      )}
    </List>
  );
}

function ProfilePage() {
    /*
        state lets a component "remember" information like user input
        ex: const [age, setAge] = useState(28);
        ex: const [name, setName] = useState('Taylor');
        ex: writing form component can use state to store written input value
        useState: declares a state vaiable that you can update directly
    */
   // useState hook to track whether post dialog is open or closed
   // 'open': boolean state true=dialog open, false=dialog closed
   // 'setState: fxn to update that state
   const [open, setOpen] = useState(false);

   // state initialize to an empty array to store a list of posts
   // 'posts': stores an array of post objects fetched from server
   // 'setPosts': updates posts state
   const [posts, setPosts] = useState([]);

   // store username
   const [username, setUsername] = useState(""); 

   // store user info
   const [userInfo, setUserInfo] = useState(null);

   // tier classification based on user's academic and industry practitioner accolades
   // used for voting power where each tier is assigned an integer which is their voting power
   const [userTier, setUserTier] = useState("");

   // switch between view and edit states
   const [isEditing, setIsEditing] = useState(false);

   // profile info that user can edit
   const [editInfo, setEditInfo] = useState({ username: "", first_name: "", last_name: "", occupation: "", university: "", });

    const [followersCount, setFollowersCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);

    const [user, setUser] = useState(null);
    const [followers, setFollowers] = useState([]);
    const [following, setFollowing] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState(null);

    const [hoveredButton, setHoveredButton] = useState(null);
    const [followingMap, setFollowingMap] = useState({});
    const [currentUserId, setCurrentUserId] = useState(null);

    const [selectedTab, setSelectedTab] = useState("Biography");

    const [isEditingBio, setIsEditingBio] = useState(false);
    const [bioText, setBioText] = useState(userInfo?.bio || "");

    const [replyView, setReplyView] = useState("main"); // "main" or "gov"
    const [replyViewUpvotes, setReplyViewUpvotes] = useState("main");
    const [postReplyView, setPostReplyView] = useState("writing"); 

    const history = useHistory();

    const [replies, setReplies] = useState([]);
    const [govComments, setGovComments] = useState([]);
    const [upvotedReplies, setUpvotedReplies] = useState([]);
    const [govUpvotedReplies, setGovUpvotedReplies] = useState([]);

    const [blogPosts, setBlogPosts] = useState([]);
    const [openBlogDialog, setOpenBlogDialog] = useState(false);
    const [blogTitle, setBlogTitle] = useState("");
    const [blogContent, setBlogContent] = useState("");

    const [selectedPost, setSelectedPost] = useState(null);

    // Book Reviews
    const [bookReviews, setBookReviews] = useState([]);
    const [openBookReviewsDialog, setOpenBookReviewsDialog] = useState(false);
    const [bookReviewTitle, setBookReviewTitle] = useState("");
    const [bookReviewContent, setBookReviewContent] = useState("");

    const [selectedBookReview, setSelectedBookReview] = useState(null);
    // Book Reviews

    const [openEditProfileDialog, setOpenEditProfileDialog] = useState(false);

    const [bioFields, setBioFields] = useState({
        bio: userInfo?.bio || "",
        education: userInfo?.education || "",
        publications: userInfo?.publications || "",
        courses: userInfo?.courses || "",
        experience: userInfo?.experience || "",
    });

    const [sortType, setSortType] = useState("latest");
    const [sortBookReviewsType, setSortBookReviewsType] = useState("latest");

    const userId = user?._id;
    const filteredGovComments = govComments.filter((post) => {
        return post.author?._id === userId;
    });

    /*useEffect(() => {
        const fetchNotificationsAndFollowing = async () => {
        try {
            const notifRes = await axios.get("/user/follow-notifications");
            const notifs = notifRes.data.followNotifications || [];
            // Fetch current user's following list
            const followRes = await axios.get("/user/following");
            const followingIds = followRes.data.following.map((f) => f._id);

            // Build a quick lookup map
            const map = {};
            notifs.forEach((n) => {
                map[n._id] = followingIds.includes(n._id);
            });
            setFollowingMap(map);
            setCurrentUserId(followRes.data.currentUserId);
        } catch (err) {
            console.error("Error fetching follow notifications or following list:", err);
        }
        };

        fetchNotificationsAndFollowing();
    }, []);*/
    /*useEffect(() => {
        const fetchFollowing = async () => {
            try {
                const res = await axios.get("/user/following");

                const followingIds = new Set(
                    res.data.following.map((f) => f._id.toString())
                );

                // Build map for O(1) lookup
                const map = {};
                followingIds.forEach((id) => {
                    map[id] = true;
                });

                setFollowingMap(map);
                setCurrentUserId(res.data.currentUserId);
            } catch (err) {
                console.error("Error fetching following list:", err);
            }
        };

        fetchFollowing();
    }, []);*/
    useEffect(() => {
        const fetchFollowing = async () => {
            try {
                // Fetch ALL following IDs for the map — but only IDs, not full objects
                // If your scale is huge (10k+), replace this with a /user/following-ids endpoint
                const res = await axios.get("/user/following", { params: { page: 1, limit: 1000 } });
                const map = {};
                res.data.following.forEach((f) => { map[f._id.toString()] = true; });
                setFollowingMap(map);
                setCurrentUserId(res.data.currentUserId);
            } catch (err) {
                console.error("Error fetching following list:", err);
            }
        };
        fetchFollowing();
    }, []);

    /*useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const res = await axios.get("/user/profile");
                const userData = res.data;
                setFollowersCount(userData.followers?.length || 0);
                setFollowingCount(userData.following?.length || 0);

                setUser(userData);
                setFollowers(userData.followers || []);
                setFollowing(userData.following || []);
            } catch (err) {
                console.error("Error loading profile:", err);
            }
        };
        fetchProfileData();
    }, []);*/
    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                const res = await axios.get("/user/profile");
                const userData = res.data;
                setFollowersCount(userData.followersCount || 0);
                setFollowingCount(userData.followingCount || 0);
                setUser(userData);
            } catch (err) {
                console.error("Error loading profile:", err);
            }
        };
        fetchProfileData();
    }, []);
   
    // Infinite list hooks
    const followersList = useInfiniteList('/user/followers');
    const followingList = useInfiniteList('/user/following');

    const dialogScrollRef = useRef(null);
    
   // fetch user info
   useEffect(() => {
    axios.get('/user/me')
        .then(res => {

            setUserInfo(res.data);
            setUserTier(res.data.tier || "");  // Load saved tier
        })
        .catch(err => console.error("Failed to fetch user info", err)); 
    }, []);

   // fetch user info
   useEffect(() => {
    axios.get('/user/info')
      .then(res => setUsername(res.data.username))
      .catch(err => console.error("Failed to load user info", err));
   }, []);

   /*// fetch user's posts once component loads 
   useEffect(() => {
        axios.get('/user/posts') // send GET request to /user/posts
            .then(response => setPosts(response.data)) // update posts with user's post data from response
            .catch(err => console.error("Failed to fetch posts", err));
   }, []);*/
    // ── Pagination state — one set per tab ──────────────────
    const [hasMore, setHasMore] = useState(true);
    const [feedLoading, setFeedLoading] = useState(false);

    const feedLoadingRef = useRef(false);
    const hasMoreRef = useRef(true);
    const cursorRef = useRef(null);

    // Map tab label to API type param
    const tabToType = {
        writing: "text",
        photos: "photo",
        videos: "video",
    };

    const fetchPosts = useCallback(async (reset = false) => {
        if (feedLoadingRef.current || !hasMoreRef.current) return;

        feedLoadingRef.current = true;
        setFeedLoading(true);

        try {
            const res = await axios.get("/user/posts", {
                params: {
                    type: tabToType[postReplyView],
                    cursor: reset ? null : cursorRef.current,
                    limit: 10,
                },
            });

            const { posts: newPosts, nextCursor, hasMore: more } = res.data;

            setPosts(prev => reset ? newPosts : [...prev, ...newPosts]);
            cursorRef.current = nextCursor;
            hasMoreRef.current = more;
            setHasMore(more);
        } catch (err) {
            console.error("Failed to fetch posts", err);
        } finally {
            feedLoadingRef.current = false;
            setFeedLoading(false);
        }
    }, [postReplyView]);

    // Reset and refetch whenever tab changes
    useEffect(() => {
        cursorRef.current = null;
        hasMoreRef.current = true;
        setPosts([]);
        setHasMore(true);
        fetchPosts(true);
    }, [postReplyView]);

    // IntersectionObserver
    const observer = useRef();
        const lastPostRef = useCallback(node => {
        if (feedLoading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMoreRef.current && !feedLoadingRef.current) {
                fetchPosts();
            }
        }, { rootMargin: "200px" });
        if (node) observer.current.observe(node);
    }, [feedLoading, fetchPosts]);

   /*useEffect(() => {
        axios.get("/user/replies")
            .then(res => setReplies(res.data))
            .catch(err => console.error("Error fetching replies", err));
    }, [selectedTab]);*/

   // refresh posts after submitting a new one
   const refreshPosts = () => {
        axios.get('/user/posts') // send GET request to /user/posts
            .then(response => setPosts(response.data)) // update posts with user's post data from response
            .catch(err => console.error("Failed to refresh posts", err));
   };
   
  

    // fill editInfo when userInfo loads
    useEffect(() => {
        if (userInfo) {
            setEditInfo({
                username: userInfo.username || "",
                first_name: userInfo.first_name || "",
                last_name: userInfo.last_name || "",
                occupation: userInfo.occupation || "",
                university: userInfo.university || "",
            });
        }
    }, [userInfo]);

    /*
    // Update when userInfo changes (useEffect)
    useEffect(() => {
        setBioFields({
            bio: userInfo?.bio || "",
            education: userInfo?.education || "",
            publications: userInfo?.publications || "",
            courses: userInfo?.courses || "",
            experience: userInfo?.experience || "",
        });
    }, [userInfo]);
    */
   useEffect(() => {
        // Only update when not editing
        if (!isEditingBio && userInfo) {
            setBioFields({
                bio: userInfo.bio || "",
                education: userInfo.education || "",
                publications: userInfo.publications || "",
                courses: userInfo.courses || "",
                experience: userInfo.experience || "",
            });
        }
    }, [userInfo, isEditingBio]);

    function flattenGovComments(govItems) {
        const list = [];

        const walk = (comment, parentMeta = {}) => {
            list.push({
            // navigation needed
            govType: parentMeta.type,          // bill | scotus | stateBill
            govId: parentMeta._id,             // gov item id
            //govTitle: parentMeta.title || parentMeta.name || parentMeta.number,
            govTitle: parentMeta.number
                ? `${parentMeta.number}: ${parentMeta.title || parentMeta.name || ""}`
                : parentMeta.title || parentMeta.name || "",
            parentContent: parentMeta.parentContent || null,
            parentAuthorUsername: parentMeta.parentAuthorUsername || null,
            parentId: parentMeta.parentId || null,

            // comment info
            _id: comment._id,
            //commentId: comment._id,
            replyId: comment._id,
            text: comment.text,
            created_at: comment.created_at,
            upvotes: comment.upvotes || 0,
            voted: comment.voted || false,
            author: comment.author || {},

            // for navigation
            commentId: comment._id,
            postId: parentMeta._id,
            type: parentMeta.type,

            rootCommentId: parentMeta.rootCommentId || comment._id,

            replies: comment.replies || [],
            });

            // walk children
            comment.replies?.forEach((r) =>
            walk(r, {
                type: parentMeta.type,
                _id: parentMeta._id,
                title: parentMeta.title,
                name: parentMeta.name,
                number: parentMeta.number,

                parentContent: comment.text,
                parentAuthorUsername: comment.author?.username || "unknown",
                parentId: comment._id,

                rootCommentId: parentMeta.rootCommentId || parentMeta.parentId || parentMeta._id,
            })
            );
        };

        govItems.forEach((item) => {
            item.comments.forEach((c) =>
            walk(c, {
                type: item.type,
                _id: item._id,
                title: item.title,
                name: item.name,
                number: item.number,
                parentId: null, 
            })
            );
        });

        return list;
    }

    function flattenGovUpvotedReplies(govItems) {
       const list = [];


       const walk = (comment, parentMeta = {}) => {
           list.push({
           // navigation needed
           govType: parentMeta.type,          // bill | scotus | stateBill
           govId: parentMeta._id,             // gov item id
           govTitle: parentMeta.number
               ? `${parentMeta.number}: ${parentMeta.title || parentMeta.name || ""}`
               : parentMeta.title || parentMeta.name || "",
           parentContent: parentMeta.parentContent || null,
           parentAuthorUsername: parentMeta.parentAuthorUsername || null,
           parentId: parentMeta.parentId || null,


           // comment info
           _id: comment._id,
           //commentId: comment._id,
           replyId: comment._id,
           text: comment.text,
           created_at: comment.created_at,
           upvotes: comment.upvotes || 0,
           voted: comment.voted || false,
           author: comment.author || {},


           // for navigation
           commentId: comment._id,
           postId: parentMeta._id,
           type: parentMeta.type,


           rootCommentId: parentMeta.rootCommentId || comment._id,


           replies: comment.replies || [],
           });


           // walk children
           comment.replies?.forEach((r) =>
           walk(r, {
               type: parentMeta.type,
               _id: parentMeta._id,
               title: parentMeta.title,
               name: parentMeta.name,
               number: parentMeta.number,


               parentContent: comment.text,
               parentAuthorUsername: comment.author?.username || "unknown",
               parentId: comment._id,


               rootCommentId: parentMeta.rootCommentId || parentMeta.parentId || parentMeta._id,
           })
           );
       };


       govItems.forEach((item) => {
           item.comments.forEach((c) =>
           walk(c, {
               type: item.type,
               _id: item._id,
               title: item.title,
               name: item.name,
               number: item.number,
               parentId: null,
           })
           );
       });


       return list;
   }

/*
    useEffect(() => {
        axios.get('/user/govReplies')
            .then(res => {
            const flat = flattenGovComments(res.data);
            setGovComments(flat);
            })
            .catch(err => console.error(err));
    }, []);*/
    /*useEffect(() => {
        axios.get('/user/govReplies')
            .then(res => {
                const normalized = res.data.map(item => ({
                    ...item,

                    // normalize naming for frontend consistency
                    govType: item.postType,
                    govId: item.postId,
                    govTitle: item.parentPost?.title || item.parentPost?.name || "" || item.parentPost?.content,

                    // ALWAYS use targetId
                    targetId: item.targetId || item._id,

                    upvotes: item.upvoteCount ?? 0,
                    voted: item.voted ?? false,
                    replies: item.replies ?? []
                }));

                setGovComments(normalized);
            })
            .catch(err => console.error(err));
    }, []);*/
    // ── Main replies state ──────────────────────────────────
    const [repliesHasMore, setRepliesHasMore] = useState(true);
    const [repliesLoading, setRepliesLoading] = useState(false);
    const repliesLoadingRef = useRef(false);
    const repliesHasMoreRef = useRef(true);
    const repliesCursorRef = useRef(null);

    // ── Gov replies state ───────────────────────────────────
    const [govHasMore, setGovHasMore] = useState(true);
    const [govLoading, setGovLoading] = useState(false);
    const govLoadingRef = useRef(false);
    const govHasMoreRef = useRef(true);
    const govCursorRef = useRef(null);

    // ── Fetch main replies ──────────────────────────────────
    const fetchReplies = useCallback(async (reset = false) => {
    if (repliesLoadingRef.current || !repliesHasMoreRef.current) return;
    repliesLoadingRef.current = true;
    setRepliesLoading(true);
    try {
        const res = await axios.get("/user/replies", {
        params: { cursor: reset ? null : repliesCursorRef.current, limit: 15 },
        });
        const { replies: newReplies, nextCursor, hasMore } = res.data;
        setReplies(prev => reset ? newReplies : [...prev, ...newReplies]);
        repliesCursorRef.current = nextCursor;
        repliesHasMoreRef.current = hasMore;
        setRepliesHasMore(hasMore);
    } catch (err) {
        console.error("Error fetching replies", err);
    } finally {
        repliesLoadingRef.current = false;
        setRepliesLoading(false);
    }
    }, []);

    // ── Fetch gov replies ───────────────────────────────────
    const fetchGovReplies = useCallback(async (reset = false) => {
    if (govLoadingRef.current || !govHasMoreRef.current) return;
    govLoadingRef.current = true;
    setGovLoading(true);
    try {
        const res = await axios.get("/user/govReplies", {
        params: { cursor: reset ? null : govCursorRef.current, limit: 15 },
        });
        const { replies: newReplies, nextCursor, hasMore } = res.data;
        // Normalize to match existing JSX field names
        const normalized = newReplies.map(item => ({
        ...item,
        upvotes: item.upvoteCount ?? 0,
        }));
        setGovComments(prev => reset ? normalized : [...prev, ...normalized]);
        govCursorRef.current = nextCursor;
        govHasMoreRef.current = hasMore;
        setGovHasMore(hasMore);
    } catch (err) {
        console.error("Error fetching gov replies", err);
    } finally {
        govLoadingRef.current = false;
        setGovLoading(false);
    }
    }, []);

    // ── Reset when tab/sub-view changes ────────────────────
    useEffect(() => {
    if (selectedTab !== "Replies") return;

    if (replyView === "main") {
        repliesCursorRef.current = null;
        repliesHasMoreRef.current = true;
        setReplies([]);
        setRepliesHasMore(true);
        fetchReplies(true);
    } else if (replyView === "gov") {
        govCursorRef.current = null;
        govHasMoreRef.current = true;
        setGovComments([]);
        setGovHasMore(true);
        fetchGovReplies(true);
    }
    }, [selectedTab, replyView]);

    // ── IntersectionObservers ───────────────────────────────
    const repliesObserver = useRef();
    const lastReplyRef = useCallback(node => {
    if (repliesLoading) return;
    if (repliesObserver.current) repliesObserver.current.disconnect();
    repliesObserver.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && repliesHasMoreRef.current && !repliesLoadingRef.current) {
        fetchReplies();
        }
    }, { rootMargin: "200px" });
    if (node) repliesObserver.current.observe(node);
    }, [repliesLoading, fetchReplies]);

    const govObserver = useRef();
    const lastGovReplyRef = useCallback(node => {
    if (govLoading) return;
    if (govObserver.current) govObserver.current.disconnect();
    govObserver.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && govHasMoreRef.current && !govLoadingRef.current) {
        fetchGovReplies();
        }
    }, { rootMargin: "200px" });
    if (node) govObserver.current.observe(node);
    }, [govLoading, fetchGovReplies]);

    /*useEffect(() => {
        axios.get("/user/upvotedReplies")
            .then(res => setUpvotedReplies(res.data))
            .catch(err => console.error("Error fetching replies", err));
    }, [selectedTab])

   useEffect(() => {
    axios.get('/user/govUpvotedReplies')
        .then(res => {
        const mapped = res.data.map(r => ({
            ...r,

            // map backend fields → frontend fields
            govId: r.postId,
            govType: r.postType,
            parentId: r.parentComment,

            // build title fallback
            govTitle:
            r.parentPost?.title ||
            r.parentPost?.content ||
            r.postType ||
            "View discussion",

            // normalize naming
            upvoteCount: r.upvoteCount,
        }));

        setGovUpvotedReplies(mapped);
        })
        .catch(err => console.error(err));
    }, []);*/
    // ── Main upvotes state ──────────────────────────────────
    const [upvotedHasMore, setUpvotedHasMore] = useState(true);
    const [upvotedLoading, setUpvotedLoading] = useState(false);
    const upvotedLoadingRef = useRef(false);
    const upvotedHasMoreRef = useRef(true);
    const upvotedCursorRef = useRef(null);

    // ── Gov upvotes state ───────────────────────────────────
    const [govUpvotedHasMore, setGovUpvotedHasMore] = useState(true);
    const [govUpvotedLoading, setGovUpvotedLoading] = useState(false);
    const govUpvotedLoadingRef = useRef(false);
    const govUpvotedHasMoreRef = useRef(true);
    const govUpvotedCursorRef = useRef(null);

    // ── Fetch main upvoted ──────────────────────────────────
    const fetchUpvoted = useCallback(async (reset = false) => {
    if (upvotedLoadingRef.current || !upvotedHasMoreRef.current) return;
    upvotedLoadingRef.current = true;
    setUpvotedLoading(true);
    try {
        const res = await axios.get("/user/upvotedReplies", {
        params: { cursor: reset ? null : upvotedCursorRef.current, limit: 15 },
        });
        const { replies: newReplies, nextCursor, hasMore } = res.data;
        setUpvotedReplies(prev => reset ? newReplies : [...prev, ...newReplies]);
        upvotedCursorRef.current = nextCursor;
        upvotedHasMoreRef.current = hasMore;
        setUpvotedHasMore(hasMore);
    } catch (err) {
        console.error("Error fetching upvoted replies", err);
    } finally {
        upvotedLoadingRef.current = false;
        setUpvotedLoading(false);
    }
    }, []);

    // ── Fetch gov upvoted ───────────────────────────────────
    const fetchGovUpvoted = useCallback(async (reset = false) => {
    if (govUpvotedLoadingRef.current || !govUpvotedHasMoreRef.current) return;
    govUpvotedLoadingRef.current = true;
    setGovUpvotedLoading(true);
    try {
        const res = await axios.get("/user/govUpvotedReplies", {
        params: { cursor: reset ? null : govUpvotedCursorRef.current, limit: 15 },
        });
        const { replies: newReplies, nextCursor, hasMore } = res.data;
        setGovUpvotedReplies(prev => reset ? newReplies : [...prev, ...newReplies]);
        govUpvotedCursorRef.current = nextCursor;
        govUpvotedHasMoreRef.current = hasMore;
        setGovUpvotedHasMore(hasMore);
    } catch (err) {
        console.error("Error fetching gov upvoted replies", err);
    } finally {
        govUpvotedLoadingRef.current = false;
        setGovUpvotedLoading(false);
    }
    }, []);

    // ── Reset when tab or sub-view changes ─────────────────
    useEffect(() => {
    if (selectedTab !== "Power Votes") return;

    if (replyViewUpvotes === "main") {
        upvotedCursorRef.current = null;
        upvotedHasMoreRef.current = true;
        setUpvotedReplies([]);
        setUpvotedHasMore(true);
        fetchUpvoted(true);
    } else if (replyViewUpvotes === "gov") {
        govUpvotedCursorRef.current = null;
        govUpvotedHasMoreRef.current = true;
        setGovUpvotedReplies([]);
        setGovUpvotedHasMore(true);
        fetchGovUpvoted(true);
    }
    }, [selectedTab, replyViewUpvotes]);

    // ── IntersectionObservers ───────────────────────────────
    const upvotedObserver = useRef();
    const lastUpvotedRef = useCallback(node => {
    if (upvotedLoading) return;
    if (upvotedObserver.current) upvotedObserver.current.disconnect();
    upvotedObserver.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && upvotedHasMoreRef.current && !upvotedLoadingRef.current) {
        fetchUpvoted();
        }
    }, { rootMargin: "200px" });
    if (node) upvotedObserver.current.observe(node);
    }, [upvotedLoading, fetchUpvoted]);

    const govUpvotedObserver = useRef();
    const lastGovUpvotedRef = useCallback(node => {
    if (govUpvotedLoading) return;
    if (govUpvotedObserver.current) govUpvotedObserver.current.disconnect();
    govUpvotedObserver.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && govUpvotedHasMoreRef.current && !govUpvotedLoadingRef.current) {
        fetchGovUpvoted();
        }
    }, { rootMargin: "200px" });
    if (node) govUpvotedObserver.current.observe(node);
    }, [govUpvotedLoading, fetchGovUpvoted]);

   useEffect(() => {
        if (selectedTab === "Blog") {
            axios.get(`/blog/user/${user?._id}`)
                .then(res => setBlogPosts(res.data))
                .catch(err => console.error(err));
        }
    }, [selectedTab]);

    useEffect(() => {
        if (selectedTab === "Book Reviews") {
            axios.get(`/bookReviews/user/${user?._id}`)
                .then(res => setBookReviews(res.data))
                .catch(err => console.error(err));
        }
    }, [selectedTab]);

    // Only show items that were actually upvoted
    const filteredGovUpvotedReplies = (govUpvotedReplies || []).filter(
        (post) => post.voted === true
    );

    // Clean nested replies
    function filterRepliesFrontend(replies = []) {
        return (replies || [])
            .map(r => ({
                ...r,
                replies: filterRepliesFrontend(r.replies || [])
            }))
            .filter(r => r.voted === true || (r.replies && r.replies.length > 0));
    }

    /*const finalGovUpvotedReplies = (filteredGovUpvotedReplies || []).map(post => ({
        ...post,
        comments: filterRepliesFrontend(post.comments || [])
    }));*/
    const finalGovUpvotedReplies = (govUpvotedReplies || []).filter(
        post => post.voted
    );


    /*const handleFollowToggle = async (e, targetId) => {
        e.stopPropagation();
        try {
        if (followingMap[targetId]) {
            // Unfollow
            await axios.post(`/user/unfollow/${targetId}`);
            setFollowingMap((prev) => ({ ...prev, [targetId]: false }));
        } else {
            // Follow
            await axios.post(`/user/follow/${targetId}`);
            setFollowingMap((prev) => ({ ...prev, [targetId]: true }));
        }
        } catch (err) {
        console.error("Follow/Unfollow error:", err);
        }
    };*/
    const handleFollowToggle = async (e, targetId) => {
        e.stopPropagation();

        try {
            if (followingMap[targetId]) {
                await axios.post(`/user/unfollow/${targetId}`);

                setFollowingMap((prev) => {
                    const updated = { ...prev };
                    delete updated[targetId];
                    return updated;
                });

            } else {
                await axios.post(`/user/follow/${targetId}`);

                setFollowingMap((prev) => ({
                    ...prev,
                    [targetId]: true
                }));
            }
        } catch (err) {
            console.error("Follow/Unfollow error:", err);
        }
    };

    // react handler that updates editInfo state as user types
    // declare a fxn that recieves the event change e from any input element
    const handleChange = e => {
        // update editInfo state with previous state object
        setEditInfo((prev) => ({
            // copy everything that is already in editInfo
            ...prev, 
            // overwrite single field input that changed
            [e.target.name]: e.target.value,
        }));
    };

  
   /*const saveChanges = async () => {
        try {
            const response = await axios.put("/user/updateTier", editInfo);

            // The updated user data comes from the server, including the new tier
            const updatedUser = response.data;

            // Update both userInfo and userTier directly
            setUserInfo(updatedUser);
            setUserTier(updatedUser.tier || "Unclassified");

            setIsEditing(false);
        } catch (err) {
            console.error("Update failed:", err);
        }
    };*/
    const saveChanges = async () => {
        try {

            const response =
            await axios.put(
                "/user/updatedProfile",
                editInfo
            );

            const updatedUser =
            response.data;

            setUserInfo(updatedUser);

            setIsEditing(false);

        } catch (err) {

            console.error(
            "Update failed:",
            err
            );
        }
    };

    /*
    const handleImageUpload = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append(type, file);

        try {
            const res = await axios.post('/users/upload-images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            });

            // Update local state
            setUserInfo(prev => ({
                ...prev,
                profileImageUrl: res.data.profileImageUrl || prev.profileImageUrl,
                bannerUrl: res.data.bannerUrl || prev.bannerUrl,
                }));
        } catch (err) {
            console.error("Image upload failed", err);
        }
    };
    */
    const bannerInputRef = useRef(null);
    const profileInputRef = useRef(null);

    const handleImageUpload = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;
        const formData = new FormData();
        if (type === 'profileImage') formData.append('profileImage', file);
        if (type === 'bannerImage') formData.append('bannerImage', file);

        try {
        const res = await axios.post('/upload-images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUserInfo(prev => ({
            ...prev,
            profileImageUrl: res.data.profileImageUrl || prev.profileImageUrl,
            bannerUrl: res.data.bannerUrl || prev.bannerUrl,
        }));
        } catch (err) {
        console.error("Image upload failed", err);
        }
    };

    /*const handleOpenDialog = (type) => {
        setDialogType(type);
        setOpenDialog(true);
    };*/
    const handleOpenDialog = (type) => {
        setDialogType(type);
        setOpenDialog(true);
        if (type === 'followers') {
            followersList.reset();
            followersList.loadMore(1);
        } else {
            followingList.reset();
            followingList.loadMore(1);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setDialogType(null);
    };

    const handleSaveBio = async () => {
        try {
            //const response = await axios.put(`/user/updateBio`, { bio: bioText });
            //setUserInfo((prev) => ({ ...prev, bio: bioText }));
            await axios.put("/user/updateBio", bioFields, { withCredentials: true });
            setUserInfo((prev) => ({ ...prev, ...bioFields }));
            setIsEditingBio(false);
        } catch (error) {
            console.error("Error updating bio:", error);
        }
    };


    // Handle field input
    //const handleBioFieldChange = (e) => {
    //    setBioFields((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    //};
    const handleBioFieldChange = (e) => {
        const { name, value } = e.target;
        setBioFields((prev) => ({ ...prev, [name]: value }));
    };

    const toggleVote = async (postId, type) => {
        try {
            const endpoint = type === "photo" 
            ? `/photos/${postId}/upvote`
            : type === "video" 
            ? `/videos/${postId}/upvote`
            : `/post/${postId}/upvote`;

            const res = await axios.post(endpoint);

            setPosts((prev) =>
            prev.map((p) =>
                p._id === postId
                ? {
                    ...p,
                    upvotes: res.data.upvotes,
                    voted: res.data.voted,
                    }
                : p
            )
            );
        } catch (err) {
            console.error("Vote error", err);
        }
    };

    const toggleReplyVote = async ({ postId, targetId, type }) => {
        try {
            let endpoint = "";

            if (type === "reply") {
                    endpoint = `/mainSocialPost/${postId}/commentReplies/${targetId}/upvote`;
            } else {
                endpoint = `/mainSocialPostComments/${targetId}/upvote`;
            }


            const res = await axios.post(endpoint);

            /*
            setReplies(prev =>
                prev.map(r =>
                    r._id === replyId
                        ? {
                            ...r,
                            ...(type === "reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                              }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                              })
                        }
                        : r
                )
            );*/
            /*setReplies(prev =>
                prev.map(r => {
                    const match =
                        type === "reply"
                            ? r._id === replyId
                            : r._id === commentId;  

                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );*/
            setReplies(prev =>
                prev.map(r =>
                    r._id === targetId
                        ? {
                            ...r,
                            upvoteCount: res.data.upvotes ?? res.data.replyUpvotes,
                            voted: res.data.voted ?? res.data.replyVoted
                        }
                        : r
                )
            );
        } catch (err) {
            console.error("Vote error", err);
        }
    };

    const toggleUpvotesVote = async ({ postId, commentId, replyId, type }) => {
        try {
            let endpoint = "";

            if (type === "post") {
                endpoint = `/post/${postId}/upvote`;
            } 
            else if (type === "comment") {
                endpoint = `/mainSocialPostComments/${commentId}/upvote`;
            } 
            else if (type === "reply") {
                endpoint = `/mainSocialPost/${postId}/commentReplies/${replyId}/upvote`;
            }

            const res = await axios.post(endpoint);

            /*setUpvotedReplies(prev =>
                prev.map(r => {
                    const match =
                        type === "reply"
                            ? r._id === replyId
                            : type === "comment"
                                ? r._id === commentId
                                : r._id === postId;

                    if (!match) return r;

                    return {
                        ...r,
                        upvotes:
                            type === "reply"
                                ? res.data.replyUpvotes
                                : type === "comment"
                                    ? res.data.upvotes
                                    : res.data.postUpvotes,
                        voted:
                            type === "reply"
                                ? res.data.replyVoted
                                : type === "comment"
                                    ? res.data.voted
                                    : res.data.postVoted
                    };
                })
            );*/
            /*
            setUpvotedReplies(prev =>
                prev.map(r => {
                    const match =
                        type === "reply"
                            ? r._id === replyId
                            : r._id === commentId;  

                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );*/
            setUpvotedReplies(prev =>
                prev.map(r => {
                    let match = false;
                    /*
                    if (type === "post") {
                        match = r.postId === postId;
                    } else if (type === "comment") {
                        match = r.commentId === commentId;
                    } else if (type === "reply") {
                        match = r.replyId === replyId;
                    }*/
                   if (type === "post") {
                        match = r.type === "post" && r.postId === postId;
                    }
                    else if (type === "comment") {
                        match = r.type === "comment" && r.commentId === commentId;
                    }
                    else if (type === "reply") {
                        match = r.type === "reply" && r.replyId === replyId;
                    }

                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "reply"
                            ? {
                                upvoteCount: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvoteCount: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );

        } catch (err) {
            console.error("Vote error", err);
        }
    };
/*
    const togglePhotoUpvotesVote = async ({ postId, commentId, replyId, type }) => {
        try {
            let endpoint = "";

            if (type === "photo") {
                endpoint = `/photos/${postId}/upvote`;
            }
            else if (type === "photo-comment") {
                endpoint = `/photoComments/${commentId}/upvote`;
            }
            else if (type === "photo-reply") {
                endpoint = `/photo/${postId}/commentReplies/${replyId}/upvote`;
            }

            const res = await axios.post(endpoint);

            setUpvotedReplies(prev =>
                prev.map(r => {
                    let match =
                        (type === "photo" && r.postId === postId) ||
                        (type === "photo-comment" && r.commentId === commentId) ||
                        (type === "photo-reply" && r.replyId === replyId);

                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "photo-reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );
        } catch (err) {
            console.error("Photo vote error", err);
        }
    };*/
    /*
    const togglePhotoUpvotesVote = async ({ postId, commentId, replyId, type }) => {
        try {
            let endpoint = "";

            if (type === "photo") {
                endpoint = `/photos/${postId}/upvote`;
            } 
            else if (type === "photo-comment") {
                endpoint = `/photoComments/${commentId}/upvote`;
            } 
            else if (type === "photo-reply") {
                endpoint = `/photo/${postId}/commentReplies/${replyId}/upvote`;
            }

            const res = await axios.post(endpoint);

            setUpvotedReplies(prev =>
                prev.map(r => {

                    // STRICT MATCHING — only match the exact entry type
                    const isPost = r.type === "photo" && type === "photo" && r.postId === postId;
                    const isComment = r.type === "photo-comment" && type === "photo-comment" && r.commentId === commentId;
                    const isReply = r.type === "photo-reply" && type === "photo-reply" && r.replyId === replyId;

                    const match = isPost || isComment || isReply;
                    if (!match) return r;

                    // Reply needs special fields
                    if (isReply) {
                        return {
                            ...r,
                            upvotes: res.data.replyUpvotes,
                            voted: res.data.replyVoted
                        };
                    }

                    // Otherwise (post or comment)
                    return {
                        ...r,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted
                    };
                })
            );

            setReplies(prev =>
                prev.map(r => {
                    // STRICT MATCHING — only match the exact entry type
                    const isPost = r.type === "photo" && type === "photo" && r.postId === postId;
                    const isComment = r.type === "photo-comment" && type === "photo-comment" && r.commentId === commentId;
                    const isReply = r.type === "photo-reply" && type === "photo-reply" && r.replyId === replyId;

                    const match = isPost || isComment || isReply;
                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "photo-reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );

        } catch (err) {
            console.error("Photo vote error", err);
        }
    };*/
    const togglePhotoUpvotesVote = async ({ postId, targetId, type }) => {
        try {
            let endpoint = "";

            if (type === "photo") {
                endpoint = `/photos/${postId}/upvote`;
            } else if (type === "photo-comment") {
                endpoint = `/photoComments/${targetId}/upvote`;
            } else if (type === "photo-reply") {
                endpoint = `/photo/${postId}/commentReplies/${targetId}/upvote`;
            }

            const res = await axios.post(endpoint);

            const newCount = res.data.upvotes ?? res.data.replyUpvotes;
            const newVoted = res.data.voted ?? res.data.replyVoted;

            // Update BOTH states using ONE consistent match
            const updateFn = (prev) =>
                prev.map(r =>
                    r._id === targetId
                        ? {
                            ...r,
                            upvoteCount: newCount, 
                            voted: newVoted
                        }
                        : r
                );

            setUpvotedReplies(updateFn);
            setReplies(updateFn);

        } catch (err) {
            console.error("Video vote error", err);
        }
    };

/*
    const toggleVideoUpvotesVote = async ({ postId, commentId, replyId, type }) => {
        try {
            let endpoint = "";

            if (type === "video") {
                endpoint = `/videos/${postId}/upvote`;
            }
            else if (type === "video-comment") {
                endpoint = `/videoComments/${commentId}/upvote`;
            }
            else if (type === "video-reply") {
                endpoint = `/video/${postId}/commentReplies/${replyId}/upvote`;
            }

            const res = await axios.post(endpoint);

            setUpvotedReplies(prev =>
                prev.map(r => {
                    let match =
                        (type === "video" && r.postId === postId) ||
                        (type === "video-comment" && r.commentId === commentId) ||
                        (type === "video-reply" && r.replyId === replyId);

                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "video-reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );
        } catch (err) {
            console.error("Video vote error", err);
        }
    };
*/
    /*const toggleVideoUpvotesVote = async ({ postId, commentId, replyId, type }) => {
        try {
            let endpoint = "";

            if (type === "video") {
                endpoint = `/videos/${postId}/upvote`;
            } 
            else if (type === "video-comment") {
                endpoint = `/videoComments/${commentId}/upvote`;
            } 
            else if (type === "video-reply") {
                endpoint = `/video/${postId}/commentReplies/${replyId}/upvote`;
            }

            const res = await axios.post(endpoint);

            setUpvotedReplies(prev =>
                prev.map(r => {
                    // STRICT MATCHING — only match the exact entry type
                    const isPost = r.type === "video" && type === "video" && r.postId === postId;
                    const isComment = r.type === "video-comment" && type === "video-comment" && r.commentId === commentId;
                    const isReply = r.type === "video-reply" && type === "video-reply" && r.replyId === replyId;

                    const match = isPost || isComment || isReply;
                    if (!match) return r;

                    // Reply uses special fields
                    if (isReply) {
                        return {
                            ...r,
                            upvotes: res.data.replyUpvotes,
                            voted: res.data.replyVoted
                        };
                    }

                    // Post + comment use standard fields
                    return {
                        ...r,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted
                    };
                })
            );

            setReplies(prev =>
                prev.map(r => {
                    // STRICT MATCHING — only match the exact entry type
                    const isPost = r.type === "video" && type === "video" && r.postId === postId;
                    const isComment = r.type === "video-comment" && type === "video-comment" && r.commentId === commentId;
                    const isReply = r.type === "video-reply" && type === "video-reply" && r.replyId === replyId;

                    const match = isPost || isComment || isReply;
                    if (!match) return r;

                    return {
                        ...r,
                        ...(type === "video-reply"
                            ? {
                                upvotes: res.data.replyUpvotes,
                                voted: res.data.replyVoted
                            }
                            : {
                                upvotes: res.data.upvotes,
                                voted: res.data.voted
                            })
                    };
                })
            );
        } catch (err) {
            console.error("Video vote error", err);
        }
    };*/
    const toggleVideoUpvotesVote = async ({ postId, targetId, type }) => {
        try {
            let endpoint = "";

            if (type === "video") {
                endpoint = `/videos/${postId}/upvote`;
            } else if (type === "video-comment") {
                endpoint = `/videoComments/${targetId}/upvote`;
            } else if (type === "video-reply") {
                endpoint = `/video/${postId}/commentReplies/${targetId}/upvote`;
            }

            const res = await axios.post(endpoint);

            const newCount = res.data.upvotes ?? res.data.replyUpvotes;
            const newVoted = res.data.voted ?? res.data.replyVoted;

            // Update BOTH states using ONE consistent match
            const updateFn = (prev) =>
                prev.map(r =>
                    r._id === targetId
                        ? {
                            ...r,
                            upvoteCount: newCount, 
                            voted: newVoted
                        }
                        : r
                );

            setUpvotedReplies(updateFn);
            setReplies(updateFn);

        } catch (err) {
            console.error("Video vote error", err);
        }
    };


    const toggleBillCommentVote = async (commentId) => {
       try {
            // hit backend endpoint that toggles a vote
            //const res = await axios.post(`/post/${postId}/upvote`);
            const res = await axios.post(`/billComments/${commentId}/upvote`);

            setGovComments(prev =>
                prev.map(post =>
                    post.commentId === commentId
                    ? {
                        ...post,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted,
                        }
                    : post
                )
            );

       } catch(err) {
           console.error("Vote error", err);
       }
   };
 
   const toggleScotusCommentVote = async (commentId) => {
       try {
               // hit backend endpoint that toggles a vote
               const res = await axios.post(`/scotusCaseComments/${commentId}/upvote`);

              setGovComments(prev =>
                prev.map(post => {
                  return post.commentId === commentId
                    ? {
                        ...post,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted,
                        }
                    : post
                })
              );


       } catch(err) {
           console.error("Vote error", err);
       }
   };

   const toggleStateBillCommentVote = async (commentId) => {
       try {
           // hit backend endpoint that toggles a vote
           const res = await axios.post(`/calBillComments/${commentId}/upvote`);

           setGovComments(prev =>
                prev.map(post => {
                  return post.commentId === commentId
                    ? {
                        ...post,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted,
                        }
                    : post
                })
              );
       } catch(err) {
           console.error("Vote error", err);
       }
   };
/*
   function updateReplyRecursive(replies, replyId, newData) {
      return (replies || []).map((r) => {
        if (r._id === replyId) {
          return { ...r, ...newData }; // update this reply
        }
        return {
          ...r,
          replies: updateReplyRecursive(r.replies, replyId, newData), // recurse deeper
        };
      });
    }

    
   const toggleBillReplyVote = async (replyId, id) => {
      try {
        const res = await axios.post(`/bill/${id}/commentReplies/${replyId}/upvote`);
        const { replyUpvotes, replyVoted } = res.data;

        setGovComments((prev) =>
          prev.map((post) => {
            if (post.type !== "bill" || post._id !== id) return post;

            return {
              ...post,
              comments: post.comments.map((c) => {
                if (c._id === replyId) {
                  // top-level reply is actually a comment
                  return { ...c, upvotes: replyUpvotes, voted: replyVoted };
                }
                return {
                  ...c,
                  replies: updateReplyRecursive(c.replies, replyId, {
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                  }),
                };
              }),
            };
          })
        );

      } catch (err) {
        console.error("Reply vote error", err);
      }
    };*/
    const toggleBillReplyVote = async (replyId, rootCommentId) => {
        try {
            const res = await axios.post(`/bill/${rootCommentId}/commentReplies/${replyId}/upvote`);
            const { replyUpvotes, replyVoted } = res.data;

            setGovComments((prev) =>
            prev.map((post) =>
                post.commentId === replyId
                ? {
                    ...post,
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                    }
                : post
            )
            );

        } catch (err) {
            console.error("Reply vote error", err);
        }
    };

    const toggleScotusReplyVote = async (replyId, rootCommentId) => {
        try {
            const res = await axios.post(`/scotusCase/${rootCommentId}/commentReplies/${replyId}/upvote`);
            const { replyUpvotes, replyVoted } = res.data;

            setGovComments((prev) =>
            prev.map((post) =>
                post.commentId === replyId
                ? {
                    ...post,
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                    }
                : post
            )
            );

        } catch (err) {
            console.error("Reply vote error", err);
        }
    };

    const toggleStateBillReplyVote = async (replyId, rootCommentId) => {
        try {
            const res = await axios.post(`/calBill/${rootCommentId}/commentReplies/${replyId}/upvote`);
            const { replyUpvotes, replyVoted } = res.data;

            setGovComments((prev) =>
            prev.map((post) =>
                post.commentId === replyId
                ? {
                    ...post,
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                    }
                : post
            )
            );

        } catch (err) {
            console.error("Reply vote error", err);
        }
    };

    const toggleBlogVote = async (postId) => {
        try {

            const res = await axios.post(`/blog/upvote/${postId}`);

            setBlogPosts((prev) =>
                prev.map((p) =>
                    p._id === postId
                    ? {
                        ...p,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted,
                        }
                    : p
                )
            );
        } catch (err) {
            console.error("Vote error", err);
        }
    };

    const toggleBookReviewsVote = async (postId) => {
        try {

            const res = await axios.post(`/bookReviews/upvote/${postId}`);

            setBookReviews((prev) =>
                prev.map((p) =>
                    p._id === postId
                    ? {
                        ...p,
                        upvotes: res.data.upvotes,
                        voted: res.data.voted,
                        }
                    : p
                )
            );
        } catch (err) {
            console.error("Vote error", err);
        }
    };

    function convertLexicalJsonToSanitizedHtml(json) {
        try {
            if (typeof json === "string") {
                return `<p>${json.replace(/\n/g, "<br>")}</p>`;
            }
            const editor = createEditor({
                nodes: [
                    HeadingNode,
                    QuoteNode,
                    ListNode,
                    ListItemNode,
                    CodeNode,
                    CodeHighlightNode,
                    LinkNode,
                    AutoLinkNode,
                    HorizontalRuleNode,
                    ImageNode
                ]
            });
            editor.setEditorState(editor.parseEditorState(json));

            let html = "";
            editor.update(() => {
                html = $generateHtmlFromNodes(editor);
            });

            // Sanitize before returning
            return DOMPurify.sanitize(html, {
                USE_PROFILES: { html: true },
                ADD_ATTR: ["target"], 
            });
        } catch (e) {
            console.error("Failed to convert Lexical JSON → HTML:", e);
            return "";
        }
    }

    const sortedPosts = [...blogPosts].sort((a, b) => {
        if (sortType === "latest") {
            return new Date(b.created_at) - new Date(a.created_at);
        }
        if (sortType === "top") {
            return b.upvotes - a.upvotes;
        }
        return 0;
    });

    const sortedBookReviews = [...bookReviews].sort((a, b) => {
        if (sortBookReviewsType === "latest") {
            return new Date(b.created_at) - new Date(a.created_at);
        }
        if (sortBookReviewsType === "top") {
            return b.upvotes - a.upvotes;
        }
        return 0;
    });

    const visitProfile = (e, userId) => {
        e.stopPropagation(); // prevent opening the post
        history.push(`/user/view/${userId}`);
    };

    const goToTopic = (topic, topicType) => {
      if (topicType == "university") {
        const parts = topic.split("/");

        // university only
        if (parts.length === 1) {
            history.push(`/mainsocial/academia/university/${encodeURIComponent(parts[0])}`);
            return;
        }

        const [university, rawType, name] = parts;
        const type = rawType.toLowerCase(); // normalize

        if (type === "major" || type === "majors") {
            history.push(
            `/mainsocial/academia/university/${encodeURIComponent(university)}/majors/${encodeURIComponent(name)}`
            );
            return;
        }

        if (type === "club" || type === "clubs") {
            history.push(
            `/mainsocial/academia/university/${encodeURIComponent(university)}/clubs/${encodeURIComponent(name)}`
            );
            return;
        }

        // safety fallback (encode parts only, not slashes)
        history.push(
            `/mainsocial/academia/university/${encodeURIComponent(university)}/${encodeURIComponent(rawType)}/${encodeURIComponent(name)}`
        );
      } else if (topicType == "academicSubject") {
        history.push(`/mainsocial/academia/academicsubjects/${topic}`)
      } else if (topicType == "thread") {
        history.push(`/mainsocial/topicthreads/${topic}`)
      }
    };

    {/*const handleDeleteVideo = async (videoId) => {
        const confirmed = window.confirm("Are you sure you want to delete this video?");
        if (!confirmed) return;

        try {
            await axios.delete(`/deleteVideo/${videoId}`);
            
            // remove from UI immediately
            setPosts(prev => prev.filter(p => p._id !== videoId));
        } catch (err) {
            console.error(err);
            alert("Failed to delete video");
        }
    };*/}
    const handleDeletePost = async (e, post) => {
        e.stopPropagation();

        const confirmed = window.confirm(
            `Are you sure you want to delete this ${post.type}?`
        );
        if (!confirmed) return;

        try {
            let endpoint = "";

            if (post.type === "video") {
                endpoint = `/videos/${post._id}`;
            } else if (post.type === "photo") {
                endpoint = `/photos/${post._id}`;
            } else {
                endpoint = `/posts/${post._id}`; // writing
            }

            await axios.delete(endpoint);

            // optimistic UI update
            setPosts(prev => prev.filter(p => p._id !== post._id));

        } catch (err) {
            console.error(err);
            alert("Failed to delete post");
        }
    };

    const handleDeleteReply = async (e, post) => {
        e.stopPropagation();

        const confirmed = window.confirm(
            `Are you sure you want to delete this reply?`
        );
        if (!confirmed) return;

        try {
            let endpoint = "";

            if (post.type === "video-comment" || post.type === "video-reply") {
                endpoint = `/videocomments/${post._id}`;
            } else if (post.type === "photo-comment" || post.type === "photo-reply") {
                endpoint = `/photocomments/${post._id}`;
            } else {
                endpoint = `/comments/${post._id}`; // text
            }

            await axios.delete(endpoint);

            // remove from UI immediately
            setReplies(prev => prev.filter(r => r._id !== post._id));

        } catch (err) {
            console.error(err);
            alert("Failed to delete reply");
        }
    };

    const handleDeleteGovComment = async (e, post) => {
        e.stopPropagation();

        const confirmed = window.confirm(
            "Are you sure you want to delete this?"
        );
        if (!confirmed) return;

        try {
            await axios.delete(`/govComments/${post._id}`);

            // optimistic UI update
            setGovComments(prev =>
            prev.filter(p => p._id !== post._id)
            );

        } catch (err) {
            console.error(err);
            alert("Failed to delete comment");
        }
    };

/*
    const toggleScotusReplyVote = async (replyId, id) => {
      try {
        const res = await axios.post(`/scotusCase/${id}/commentReplies/${replyId}/upvote`);
        const { replyUpvotes, replyVoted } = res.data;

        setGovComments((prev) =>
          prev.map((post) => {
            if (post.type !== "scotus" || post._id !== id) return post;

            return {
              ...post,
              comments: post.comments.map((c) => {
                if (c._id === replyId) {
                  // top-level reply is actually a comment
                  return { ...c, upvotes: replyUpvotes, voted: replyVoted };
                }
                return {
                  ...c,
                  replies: updateReplyRecursive(c.replies, replyId, {
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                  }),
                };
              }),
            };
          })
        );
      } catch (err) {
        console.error("Reply vote error", err);
      }
    };

    const toggleStateBillReplyVote = async (replyId, id) => {
       try {
        const res = await axios.post(`/calBill/${id}/commentReplies/${replyId}/upvote`);
        const { replyUpvotes, replyVoted } = res.data;

         setGovComments((prev) =>
          prev.map((post) => {
            if (post.type !== "stateBill" || post._id !== id) return post;

            return {
              ...post,
              comments: post.comments.map((c) => {
                if (c._id === replyId) {
                  // top-level reply is actually a comment
                  return { ...c, upvotes: replyUpvotes, voted: replyVoted };
                }
                return {
                  ...c,
                  replies: updateReplyRecursive(c.replies, replyId, {
                    upvotes: replyUpvotes,
                    voted: replyVoted,
                  }),
                };
              }),
            };
          })
        );
        } catch (err) {
          console.error("Reply vote error", err);
        }
      };
      */

     const toggleGovVote = async ({ govType, govId, targetId }) => {
        try {
            let endpoint = "";

            // routing map
            const routeMap = {
                congressionalBill: `/bill/${govId}`,
                scotus: `/scotusCase/${govId}`,
                stateBill: `/calBill/${govId}`,
                houseOfRepElection: `/elections/USHouseOfRep/${govId}`,
                senateElection: `/elections/USSenate/${govId}`,
                gubernatorialElection: `/elections/GubernatorialElections/${govId}`,
                stateLegislatureElection: `/elections/StateLegislature/${govId}`,
                statewideOfficesElection: `/elections/StatewideOffices/${govId}`,
                govPost: `/govPost/${govId}`
            };

            const base = routeMap[govType];
            if (!base) {
                console.warn("Unknown govType:", govType);
                return;
            }

            endpoint = `${base}/commentReplies/${targetId}/upvote`;

            const res = await axios.post(endpoint);

            const newUpvotes = res.data.upvotes ?? res.data.replyUpvotes;
            const newVoted = res.data.voted ?? res.data.replyVoted;

            // Flat state update
            setGovComments(prev =>
                prev.map(p =>
                    p.targetId === targetId
                        ? {
                            ...p,
                            upvoteCount: newUpvotes,
                            voted: newVoted
                        }
                        : p
                )
            );

            setGovUpvotedReplies(prev =>
                prev.map(p =>
                    p.targetId === targetId
                        ? {
                            ...p,
                            upvoteCount: newUpvotes,
                            voted: newVoted
                        }
                        : p
                )
            );

        } catch (err) {
            console.error("Gov vote error:", err);
        }
    };

    const navigateToUpvote = (post) => {
        if (!post?.postId) {
            console.error("Missing postId:", post);
            return;
        }

        const root = post.postId;
        const parentId = post.parentId;
        const commentId = post.commentId;
        const replyId = post.replyId;

        switch (post.type) {
            // ---------------- POSTS ----------------
            case "post":
                history.push(`/post/${post.postId}/comments`);
                break;

            case "comment":
                return history.push(
                    `/mainSocial/${root}/comments/${post.commentId}`
                );

            case "reply":
                return history.push(
                    `/mainSocial/${root}/comments/${post.replyId}`
                );
            /*case "post":
                return history.push(`/post/${root}/comments`);

            case "comment": {
                const id = commentId || parentId;

                if (!id) {
                    console.error("Missing comment ID:", post);
                    return history.push(`/post/${root}/comments`);
                }

                return history.push(`/mainSocial/${root}/comments/${id}`);
            }

            case "reply": {
                const id = replyId || commentId || parentId;

                if (!id) {
                    console.error("Missing reply ID:", post);
                    return history.push(`/post/${root}/comments`);
                }

                return history.push(`/mainSocial/${root}/comments/${id}`);
            }*/

            // ---------------- PHOTOS ----------------
            case "photo":
                return history.push(`/photos/${root}/comments`);

            case "photo-comment":
                if (!commentId) return history.push(`/photos/${root}/comments`);
                return history.push(`/photo/${root}/comments/${commentId}`);

            case "photo-reply":
                if (!replyId) return history.push(`/photos/${root}/comments`);
                return history.push(`/photo/${root}/comments/${replyId}`);

            // ---------------- VIDEOS ----------------
            case "video":
                return history.push(`/videos/${root}/comments`);

            case "video-comment":
                if (!commentId) return history.push(`/videos/${root}/comments`);
                return history.push(`/video/${root}/comments/${commentId}`);

            case "video-reply":
                if (!replyId) return history.push(`/videos/${root}/comments`);
                return history.push(`/video/${root}/comments/${replyId}`);

            default:
                console.warn("Unknown type:", post.type);
                return history.push(`/post/${root}/comments`);
        }
    };


    if (!user) return <Typography>Loading profile...</Typography>;

   return (
    <div
            style={{
                padding: '20px',
                backgroundColor: '#FFFDF8',
                minHeight: '100vh',  // ensures it covers the full viewport height
                boxSizing: 'border-box', // ensures padding doesn't affect layout
                position: 'relative',
            }}
        >
            <Box
  sx={{
    position: "absolute",
    top: 40,          //  aligns with card top (matches mt={5})
    left: 20,         //  same as container padding
    zIndex: 10,
  }}
>
  <Tooltip title="Lady Wisdom" arrow>
    <Box
      component="img"
      src={logo}
      alt="Logo"
      onClick={() => history.push("/home")}
      sx={{
        height: 90,
        width: "auto",
        borderRadius: "10px",
        cursor: "pointer",
        objectFit: "contain",
      }}
    />
  </Tooltip>
</Box>
        <>
        {/* Profile Card */}
        {/*<Box display="flex" justifyContent="center" mt={5} px={2}>*/}
        <Box display="flex" flexDirection="column" alignItems="center" mt={5} px={2}>
            <Box sx={{ width: '100%', maxWidth: 800 }}>
            <Card sx={{ width: '100%', boxShadow: 3 }}>
            {/*<Card sx={{ width: '100%', maxWidth: 800, boxShadow: 3 }}>*/}
                <Box sx={{ position: 'relative', height: 200 }}>
                {/* Banner image */}
                <img
                    src={userInfo?.bannerUrl || '/default-banner.jpg'}
                    alt="Banner"
                    style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    }}
                />

                {/* Visible "Change Banner" button */}
                <Box
                    onClick={() => bannerInputRef.current.click()}
                    sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: 12,
                    cursor: 'pointer',
                    }}
                >
                    Change Banner
                </Box>
                <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImageUpload(e, 'bannerImage')}
                />

                {/* Profile picture */}
                <Avatar
                    src={userInfo?.profileImageUrl || '/default-avatar.jpg'}
                    alt="Profile"
                    sx={{
                    width: 100,
                    height: 100,
                    position: 'absolute',
                    bottom: -50,
                    left: 24,
                    border: '3px solid white',
                    }}
                />

                {/* Visible "Change Photo" button */}
                <Box
                    onClick={() => profileInputRef.current.click()} 
                    sx={{
                    position: 'absolute',
                    //bottom: -40,
                    bottom: 8,
                    left: 132,  // adjust to look good next to avatar
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    px: 1,
                    py: 0.3,
                    borderRadius: 1,
                    fontSize: 12,
                    cursor: 'pointer',
                    }}
                >
                    Change Photo
                </Box>
                <input
                    ref={profileInputRef}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleImageUpload(e, 'profileImage')}
                />
                </Box>
                
                {/*
                <CardContent sx={{ pt: 6 }}>
                    <Typography variant="h6">{userInfo?.first_name} {userInfo?.last_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {userInfo?.occupation || 'Your occupation'}
                    </Typography>
                </CardContent>
                */}
                {/*}
                <CardContent
                    sx={{
                        pt: 6,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-end',
                    }}
                    >
                    <Box>
                        <Typography variant="h6">
                        {userInfo?.first_name} {userInfo?.last_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                        {userInfo?.occupation || 'Your occupation'}
                        </Typography>
                    </Box>

                    <Box textAlign="right">
                        <Typography variant="body2" color="text.primary">
                            {userInfo?.university || 'Your university'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {userInfo?.company || 'Your company'}
                        </Typography>
                    </Box>
                </CardContent>
                */}
                <CardContent sx={{ pt: 6, position: 'relative' }}>
                    {/* Top-right email */}
                    <Box
                        sx={{
                            position: 'absolute',
                            //top: 0,
                            top: 10,
                            right: 16,
                        }}
                    >
                        <Typography variant="body2" color="text.primary">
                            Voting Power: {userTier || 'null'}
                        </Typography>
                    </Box>

                    {/* Bottom row: name/occupation on left, company/university on right */}
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-end',
                        }}
                    >
                        {/* Left side */}
                        <Box>
                            <Typography variant="h6">
                                {userInfo?.first_name} {userInfo?.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                                @{userInfo?.username}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {userInfo?.occupation || 'Your occupation'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                <Link
                                    component="button"
                                    underline="hover"
                                    onClick={() => handleOpenDialog("followers")}
                                >
                                    {followersCount} Followers
                                </Link> 
                                <Link
                                    component="button"
                                    underline="hover"
                                    onClick={() => handleOpenDialog("following")}
                                    sx={{ mx: 1.5 }}
                                >
                                        {followingCount} Following
                                </Link>
                            </Typography>
                            {/* ====================== POPUP DIALOG ====================== */}
                            {/*<Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                                <DialogTitle>
                                    {dialogType === "followers" ? "Followers" : "Following"}
                                </DialogTitle>

                                <DialogContent 
                                    dividers
                                    sx={{
                                        backgroundColor: "#FFFDF8"
                                    }}
                                >
                                    <List>
                                    {(dialogType === "followers" ? followers : following).map((u) => {
                                        //const isFollowing = followingMap[u._id] || false;
                                        const isFollowing = !!followingMap[u._id];
                                        const isHovered = hoveredButton === u._id;

                                        return (
                                        <ListItem
                                            key={u._id}
                                            onClick={(e) => visitProfile(e, u._id)}
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                py: 1.2,
                                                px: 1.5,
                                                borderRadius: 2,
                                                cursor: "pointer",
                                                transition: "background-color 0.2s ease",

                                                "&:hover": {
                                                    backgroundColor: "#f5f5f5"
                                                }
                                            }}
                                        >
                                            
                                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                            <ListItemAvatar>
                                                <Avatar src={u.profileImageUrl || "/default-avatar.jpg"} />
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={`${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unknown"}
                                                secondary={`@${u.username}`}
                                            />
                                            </Box>

                                            
                                            {u._id !== currentUserId && (
                                            <Button
                                                size="small"
                                                variant={isFollowing ? "contained" : "outlined"}
                                                onClick={(e) => handleFollowToggle(e, u._id)}
                                                onMouseEnter={() => setHoveredButton(u._id)}
                                                onMouseLeave={() => setHoveredButton(null)}
                                                sx={{
                                                alignSelf: "center",
                                                minWidth: "100px",
                                                textTransform: "none",
                                                transition: "all 0.2s ease-in-out",
                                                backgroundColor:
                                                    isFollowing && isHovered
                                                    ? "error.main"
                                                    : isFollowing
                                                    ? "primary.main"
                                                    : "transparent",
                                                color:
                                                    isFollowing && isHovered
                                                    ? "#fff"
                                                    : isFollowing
                                                    ? "#fff"
                                                    : "primary.main",
                                                borderColor: "primary.main",
                                                "&:hover": {
                                                    backgroundColor:
                                                    isFollowing && isHovered
                                                        ? "error.dark"
                                                        : "primary.light",
                                                },
                                                }}
                                            >
                                                {isFollowing
                                                ? isHovered
                                                    ? "Unfollow"
                                                    : "Following"
                                                : "Follow"}
                                            </Button>
                                            )}
                                        </ListItem>
                                        );
                                    })}

                                    {(dialogType === "followers" ? followers : following).length === 0 && (
                                        <Typography align="center" sx={{ mt: 2 }}>
                                        No {dialogType} yet.
                                        </Typography>
                                    )}
                                    </List>
                                </DialogContent>
                            </Dialog>*/}
                            <Dialog
                                open={openDialog}
                                onClose={handleCloseDialog}
                                fullWidth
                                maxWidth="xs"
                            >
                                <DialogTitle>
                                    {dialogType === "followers" ? "Followers" : "Following"}
                                </DialogTitle>
                                <DialogContent
                                    ref={dialogScrollRef}    
                                    dividers
                                    sx={{ backgroundColor: "#FFFDF8", p: 0, overflowY: 'auto', maxHeight: 480 }}
                                >
                                    <InfiniteUserList
                                        key={dialogType}
                                        scrollRef={dialogScrollRef}
                                        items={dialogType === 'followers' ? followersList.items : followingList.items}
                                        hasMore={dialogType === 'followers' ? followersList.hasMore : followingList.hasMore}
                                        loading={dialogType === 'followers' ? followersList.loading : followingList.loading}
                                        onLoadMore={dialogType === 'followers' ? followersList.loadMore : followingList.loadMore}
                                        currentUserId={currentUserId}
                                        followingMap={followingMap}
                                        hoveredButton={hoveredButton}
                                        onHoverEnter={setHoveredButton}
                                        onHoverLeave={() => setHoveredButton(null)}
                                        onFollowToggle={handleFollowToggle}
                                        onVisitProfile={visitProfile}
                                        emptyLabel={`No ${dialogType} yet.`}
                                    />
                                </DialogContent>
                            </Dialog>

                            {/*
                            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                                <DialogTitle>
                                {dialogType === "followers" ? "Followers" : "Following"}
                                </DialogTitle>
                                <DialogContent dividers>
                                <List>
                                    {(dialogType === "followers" ? followers : following).map((u) => (
                                    <ListItem key={u._id} alignItems="flex-start">
                                        <ListItemAvatar>
                                        <Avatar src={u.profileImageUrl || "/default-avatar.jpg"} />
                                        </ListItemAvatar>
                                        <ListItemText
                                        primary={`${u.first_name || ""} ${u.last_name || ""}`.trim() || "Unknown"}
                                        secondary={`@${u.username}`}
                                        />
                                    </ListItem>
                                    ))}
                                    {(dialogType === "followers" ? followers : following).length === 0 && (
                                    <Typography align="center" sx={{ mt: 2 }}>
                                        No {dialogType} yet.
                                    </Typography>
                                    )}
                                </List>
                                </DialogContent>
                            </Dialog>
                            */}
                        </Box>

                        {/* Right side */}
                        <Box textAlign="right">
                            <Typography variant="body2" color="text.primary">
                                {userInfo?.university || 'Your university'}
                            </Typography>
                            {/*<Typography variant="body2" color="text.secondary">
                                {userInfo?.company || 'Your company'}
                            </Typography>*/}
                        </Box>
                    </Box>
                </CardContent>
            </Card>
            <Button
                sx={{
                position: "absolute",
                top: 70,       // Move inside corner
                right: 350,     // Move inside corner
                width: 42,
                height: 42,
                minWidth: 42,
                borderRadius: "50%",
                backgroundColor: "white",
                border: "1px solid #ccc",
                zIndex: 10,
                "&:hover": { backgroundColor: "#f5f5f5" },
                }}
                //onClick={() => history.push("/settings")}
                onClick={() => setOpenEditProfileDialog(true)}
            >
            </Button>
            {/* ===== Profile Section Buttons ===== */}
            <Box
                display="flex"
                justifyContent="center"
                mt={6}
                gap={3}
                >
                {["Biography", "Posts", "Replies", "Power Votes", "Blog", "Book Reviews"].map((tab) => (
                    <Button
                    key={tab}
                    variant={selectedTab === tab ? "contained" : "outlined"}
                    onClick={() => setSelectedTab(tab)}
                    sx={{
                        textTransform: "none",
                        //minWidth: 120,
                        minWidth: 130,       
                        height: 40,
                        whiteSpace: "nowrap",
                        fontWeight: selectedTab === tab ? "bold" : "normal",
                        transition: "all 0.2s ease-in-out",
                        backgroundColor:
                        selectedTab === tab ? "primary.main" : "transparent",
                        color: selectedTab === tab ? "#fff" : "primary.main",
                        borderColor: "primary.main",
                        "&:hover": {
                        backgroundColor:
                            selectedTab === tab ? "primary.dark" : "primary.light",
                        },
                    }}
                    >
                    {tab}
                    </Button>
                ))}
                </Box>

                {/* ===== Tab Content ===== */}
                <Box
                display="flex"
                justifyContent="center"
                mt={4}
                >
                <Box
                    sx={{
                    width: "100%",
                    maxWidth: 800,
                    p: 3,
                    textAlign: "center",
                    //border: "1px solid #ddd",
                    //borderRadius: 2,
                    //backgroundColor: "#fff",
                    //boxShadow: 1,
                    //border: selectedTab === "Posts" ? "none" : "1px solid #ddd",
                    //borderRadius: selectedTab === "Posts" ? 0 : 2,
                    //backgroundColor: selectedTab === "Posts" ? "transparent" : "#fff",
                    //boxShadow: selectedTab === "Posts" ? "none" : 1,
                    border: ["Posts", "Replies", "Power Votes", "Blog", "Book Reviews"].includes(selectedTab) ? "none" : "1px solid #ddd",
                    borderRadius: ["Posts", "Replies", "Power Votes", "Blog", "Book Reviews"].includes(selectedTab) ? 0 : 2,
                    backgroundColor: ["Posts", "Replies", "Power Votes", "Blog", "Book Reviews"].includes(selectedTab) ? "transparent" : "#fff",
                    boxShadow: ["Posts", "Replies", "Power Votes", "Blog", "Book Reviews"].includes(selectedTab) ? "none" : 1,
                    }}
                >
                    {/*{selectedTab === "Biography" && (
                        <Box textAlign="left">
                            <Typography variant="h6" gutterBottom>
                                About my intellectual self... 
                            </Typography>

                            {!isEditingBio ? (
                            <>
                                <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
                                    {userInfo?.bio || "Tell us something about your intellectual self!"}
                                </Typography>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ mt: 2 }}
                                    onClick={() => setIsEditingBio(true)}
                                >
                                    Edit
                                </Button>
                            </>
                            ) : (
                            <>
                                <TextField
                                    multiline
                                    fullWidth
                                    minRows={4}
                                    value={bioText}
                                    onChange={(e) => setBioText(e.target.value)}
                                    variant="outlined"
                                    placeholder="Write something about your intellectual self..."
                                />
                                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleSaveBio}
                                >
                                    Save
                                </Button>
                                <Button
                                    variant="text"
                                    size="small"
                                    onClick={() => {
                                    setIsEditingBio(false);
                                    setBioText(userInfo?.bio || "");
                                    }}
                                >
                                    Cancel
                                </Button>
                                </Box>
                            </>
                            )}
                        </Box>
                    )}*/}
                    {/*}
                    {selectedTab === "Biography" && (
                        <Box textAlign="left" sx={{ position: "relative" }}>
                            <Box sx={{ position: "absolute", top: 8, right: 8 }}>
                                {!isEditingBio ? (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => setIsEditingBio(true)}
                                    >
                                    Edit
                                    </Button>
                                ) : (
                                    <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={{ mr: 1 }}
                                        onClick={handleSaveBio}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="text"
                                        color="error"
                                        size="small"
                                        onClick={() => {
                                            setIsEditingBio(false);
                                            setBioText(userInfo?.bio || "");
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    </>
                                )}
                            </Box>

                            {!isEditingBio ? (
                            <>
                                <SectionDisplay title="About" content={userInfo?.bio} />
                                <SectionDisplay title="Education" content={userInfo?.education} />
                                <SectionDisplay title="Publications" content={userInfo?.publications} />
                                <SectionDisplay title="Courses" content={userInfo?.courses} />
                                <SectionDisplay title="Experience" content={userInfo?.experience} />
                            </>
                            ) : (
                            <>
                                <SectionEditor
                                label="About"
                                name="bio"
                                //value={userInfo?.bio || ""}
                                value={bioFields.bio}
                                onChange={handleBioFieldChange}
                                />
                                <SectionEditor
                                label="Education"
                                name="education"
                                //value={userInfo?.education || ""}
                                value={bioFields.education}
                                onChange={handleBioFieldChange}
                                />
                                <SectionEditor
                                label="Publications"
                                name="publications"
                                //value={userInfo?.publications || ""}
                                value={bioFields.publications}
                                onChange={handleBioFieldChange}
                                />
                                <SectionEditor
                                label="Courses"
                                name="courses"
                                //value={userInfo?.courses || ""}
                                value={bioFields.courses}
                                onChange={handleBioFieldChange}
                                />
                                <SectionEditor
                                label="Experience"
                                name="experience"
                                //value={userInfo?.experience || ""}
                                value={bioFields.experience}
                                onChange={handleBioFieldChange}
                                />
                            </>
                            )}
                        </Box>
                        )}*/}
                    {selectedTab === "Biography" && (
                        <Box textAlign="left" sx={{ position: "relative" }}>
                            {/* ===== Top Right Buttons ===== */}
                            <Box sx={{ position: "absolute", top: -3, right: -3 }}>
                                {!isEditingBio ? (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                        setBioFields({
                                            bio: userInfo?.bio || "",
                                            education: userInfo?.education || "",
                                            publications: userInfo?.publications || "",
                                            courses: userInfo?.courses || "",
                                            experience: userInfo?.experience || "",
                                        });
                                        setIsEditingBio(true)
                                    }}
                                >
                                    Edit
                                </Button>
                                ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        sx={{ mr: 1 }}
                                        onClick={handleSaveBio}
                                    >
                                    Save
                                    </Button>
                                    <Button
                                    variant="text"
                                    color="error"
                                    size="small"
                                    onClick={() => {
                                        setIsEditingBio(false);
                                        setBioFields({
                                        bio: userInfo?.bio || "",
                                        education: userInfo?.education || "",
                                        publications: userInfo?.publications || "",
                                        courses: userInfo?.courses || "",
                                        experience: userInfo?.experience || "",
                                        });
                                    }}
                                    >
                                    Cancel
                                    </Button>
                                </>
                                )}
                            </Box>

                            {/* ===== Individual Fields Inline ===== */}
                            <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                About
                            </Typography>
                            {isEditingBio ? (
                                <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                name="bio"
                                value={bioFields.bio}
                                onChange={handleBioFieldChange}
                                variant="outlined"
                                placeholder="Enter your biography..."
                                sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography
                                //variant="body2"
                                variant="body1" 
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: bioFields.bio ? "inherit" : "text.secondary",
                                }}
                                >
                                {bioFields.bio || "No biography provided."}
                                </Typography>
                            )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Education
                            </Typography>
                            {isEditingBio ? (
                                <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                name="education"
                                value={bioFields.education}
                                onChange={handleBioFieldChange}
                                variant="outlined"
                                placeholder="Enter your education..."
                                sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography
                                //variant="body2"
                                variant="body1"
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: bioFields.education ? "inherit" : "text.secondary",
                                }}
                                >
                                {bioFields.education || "No education provided."}
                                </Typography>
                            )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Publications
                            </Typography>
                            {isEditingBio ? (
                                <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                name="publications"
                                value={bioFields.publications}
                                onChange={handleBioFieldChange}
                                variant="outlined"
                                placeholder="Enter your publications..."
                                sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography
                                //variant="body2"
                                variant="body1"
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: bioFields.publications ? "inherit" : "text.secondary",
                                }}
                                >
                                {bioFields.publications || "No publications provided."}
                                </Typography>
                            )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Favorite Courses
                            </Typography>
                            {isEditingBio ? (
                                <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                name="courses"
                                value={bioFields.courses}
                                onChange={handleBioFieldChange}
                                variant="outlined"
                                placeholder="Enter your courses..."
                                sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography
                                //variant="body2"
                                variant="body1"
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: bioFields.courses ? "inherit" : "text.secondary",
                                }}
                                >
                                {bioFields.courses || "No courses provided."}
                                </Typography>
                            )}
                            </Box>

                            <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                                Experience
                            </Typography>
                            {isEditingBio ? (
                                <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                name="experience"
                                value={bioFields.experience}
                                onChange={handleBioFieldChange}
                                variant="outlined"
                                placeholder="Enter your experience..."
                                sx={{ mt: 1 }}
                                />
                            ) : (
                                <Typography
                                //variant="body2"
                                variant="body1"
                                sx={{
                                    whiteSpace: "pre-line",
                                    color: bioFields.experience ? "inherit" : "text.secondary",
                                }}
                                >
                                {bioFields.experience || "No experience provided."}
                                </Typography>
                            )}
                            </Box>
                            </Box>

                    )}
                    {selectedTab === "Posts" && (
                        <Box sx={{ textAlign: "left" }}>
                            <Box display="flex" gap={2} mb={2}>
                               <Button
                                   variant={postReplyView === "writing" ? "contained" : "outlined"}
                                   onClick={() => setPostReplyView("writing")}
                               >
                                   Writing
                               </Button>
                               <Button
                                   variant={postReplyView === "photos" ? "contained" : "outlined"}
                                   onClick={() => setPostReplyView("photos")}
                               >
                                   Photos
                               </Button>
 				                <Button
                                   variant={postReplyView === "videos" ? "contained" : "outlined"}
                                   onClick={() => setPostReplyView("videos")}
                               >
                                   Videos
                               </Button>
                           </Box>
                           {postReplyView === "writing" && (
                            <>
                            {posts.filter(p => p.type !== "photo" && p.type !== "video").length === 0 ? (
                            <Typography
                                variant="body1"
                                sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                            >
                                No posts from followed users.
                            </Typography>
                            ) : (
                            posts.map((post, idx) => {
                                const isLast = idx === posts.length - 1;
                                return (
                                <Box
                                key={post._id.toString()}
                                ref={isLast ? lastPostRef : null}
                                onClick={() => history.push(`/post/${post._id}/comments`)}
                                /*onClick={() =>
                                    history.push(
                                        post.type === "photo"
                                        ? `/photos/${post._id}/comments` 
                                        : post.type === "video"
                                        ? `/videos/${post._id}/comments`
                                        : `/post/${post._id}/comments`
                                    )
                                }*/
                                p={2}
                                my={2}
                                border="1px solid #ccc"
                                borderRadius="8px"
                                sx={{
                                    position: "relative",
                                    maxWidth: 900,
                                    mx: "auto",
                                    my: 2,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#f9f9f9" },
                                }}
                                >
                                <Typography 
                                    variant="overline" 
                                    color="primary"
                                    //onClick={(e) => {
                                    //    e.stopPropagation(); 
                                    //    history.push(`/academicSubjects/${post.topic}`)
                                    //}}
                                    onClick={(e) => {
                                        e.stopPropagation(); 
                                        goToTopic(post.topic, post.topicType);
                                    }}
                                    sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                >
                                    {post.topic}
                                </Typography>
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                    <Avatar
                                    src={post.author?.profileImageUrl || "/default-avatar.jpg"}
                                    alt={`${post.author?.first_name || ""} ${post.author?.last_name || ""}`}
                                    sx={{ width: 62, height: 62 }}
                                    />
                                    <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {post.author?.first_name && post.author?.last_name
                                        ? `${post.author.first_name} ${post.author.last_name}`
                                        : "Unknown Name"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        @{post.author?.username || "unknown"}
                                    </Typography>

                                    {/*<Typography
                                        variant="body1"
                                        sx={{ mt: 1, whiteSpace: "pre-line" }}
                                    >
                                        {post.content}
                                    </Typography>*/}
                                    {/* Text post */}
                                    {post.type === "text" && (
                                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                        {post.content}
                                    </Typography>
                                    )}

                                    {/* Photo post */}
                                    {/*{post.type === "photo" && (
                                    <>
                                        <CardMedia
                                        component="img"
                                        image={`http://localhost:3000${post.imageUrl}`}
                                        alt="User photo"
                                        sx={{
                                            maxWidth: 900,
                                            width: "100%",
                                            display: "block",
                                            margin: "16px auto 0 auto",
                                            borderRadius: 2,
                                            objectFit: "contain",
                                        }}
                                        />

                                        {post.description && (
                                        <Typography
                                            variant="body1"
                                            sx={{ mt: 1, whiteSpace: "pre-line" }}
                                        >
                                            {post.description}
                                        </Typography>
                                        )}
                                    </>
                                    )}

                                    {post.type === "video" && (
                                        <>
                                        <Typography variant="subtitle1">{post.title}</Typography>
                                        <CardMedia
                                            component="img"
                                            controls
                                            src={`http://localhost:3000${post.thumbnailUrl}`}
                                            sx={{
                                            maxWidth: 900,
                                            width: "100%",
                                            display: "block",
                                            margin: "16px auto 0 auto",
                                            borderRadius: 2,
                                            objectFit: "contain",
                                            }}
                                        />
                                        
                                        </>
                                    )}

                                    {post.type === "video" && (
                                        <Button
                                            size="small"
                                            color="error"
                                            sx={{ position: "absolute", top: 8, right: 8 }}
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteVideo(post._id);
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    )}*/}
                                    <Button
                                                            size="small"
                                                            color="error"
                                                            sx={{ position: "absolute", top: 8, right: 8 }}
                                                            onClick={(e) => handleDeletePost(e, post)}
                                                        >
                                                            Delete
                                                        </Button>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        {`${new Date(post.created_at).toLocaleString()}, ${
                                        post.commentCount
                                        } ${post.commentCount === 1 ? "reply" : "replies"}`}
                                    </Typography>
                                    </Box>
                                </Box>

                                {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                    <Button
                                    size="small"
                                    variant={post.voted ? "contained" : "outlined"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleVote(post._id, post.type);
                                    }}
                                    >
                                    ▲ {post.voted ? "Unvote" : "Upvote"}
                                    </Button>
                                    <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                </Box>*/}
                                <Box mt={2} display="flex" alignItems="center" gap={1}>
                                    {post.voted ? (
                                        <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            "&:hover .voted-btn": {
                                                opacity: 0,
                                            },
                                            "&:hover .undo-btn": {
                                                opacity: 1,
                                                pointerEvents: "auto",
                                            },
                                        }}
                                    >
                                            <Button
                                                size="small"
                                                className="voted-btn"
                                                variant="contained"
                                                color="success"
                                                disabled
                                                sx={{
                                                transition: "opacity 0.2s ease",
                                                }}
                                            >
                                            Power Voted
                                        </Button>
                                        <Button
                                                size="small"
                                                className="undo-btn"
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                inset: 0,
                                                position: "absolute",
                                                transition: "opacity 0.2s ease",
                                                opacity: 0,
                                                pointerEvents: "none",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleVote(post._id, post.type);
                                            }}
                                        >
                                                Undo
                                        </Button>
                                    </Box>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            toggleVote(post._id, post.type);}
                                        }
                                        >
                                            ▲ Power Vote
                                        </Button>
                                    )}
                                    <Typography variant="body2">{post.upvotes} Power Votes</Typography>
                            </Box>
                                </Box>
                            );
                            }) 
                        )}
                            </> )}
                            {postReplyView === "videos" && (
                                <>
                                    {posts.filter(p => p.type === "video").length === 0 ? (
                                    <Typography
                                        variant="body1"
                                        sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                                    >
                                        No video posts from followed users.
                                    </Typography>
                                    ) : (
                                    posts.map((post, idx) => {
                                    const isLast = idx === posts.length - 1;
                                    return (
                                    <Box
                                            key={post._id.toString()}
                                            ref={isLast ? lastPostRef : null}
                                            onClick={() => history.push(`/videos/${post._id}/comments`)}
                                    
                                            p={2}
                                            my={2}
                                            border="1px solid #ccc"
                                            borderRadius="8px"
                                            sx={{
                                                position: "relative",
                                                maxWidth: 900,
                                                mx: "auto",
                                                my: 2,
                                                cursor: "pointer",
                                                "&:hover": { backgroundColor: "#f9f9f9" },
                                            }}
                                            >
                                            
                                            <Box display="flex" alignItems="flex-start" gap={1}>
                                                <Avatar
                                                src={post.author?.profileImageUrl || "/default-avatar.jpg"}
                                                alt={`${post.author?.first_name || ""} ${post.author?.last_name || ""}`}
                                                sx={{ width: 62, height: 62 }}
                                                />
                                                <Box>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {post.author?.first_name && post.author?.last_name
                                                    ? `${post.author.first_name} ${post.author.last_name}`
                                                    : "Unknown Name"}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    @{post.author?.username || "unknown"}
                                                </Typography>

                                                <Typography variant="subtitle1">{post.title}</Typography>
                                                    <CardMedia
                                                        component="img"
                                                        controls
                                                        src={post.thumbnailUrl}
                                                        sx={{
                                                        maxWidth: 900,
                                                        width: "100%",
                                                        display: "block",
                                                        margin: "16px auto 0 auto",
                                                        borderRadius: 2,
                                                        objectFit: "contain",
                                                        }}
                                                    />
                                                    
                                                
                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        sx={{ position: "absolute", top: 8, right: 8 }}
                                                        onClick={(e) => handleDeletePost(e, post)}
                                                    >
                                                        Delete
                                                    </Button>
                                                
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ display: "block", mt: 0.5 }}
                                                >
                                                    {`${new Date(post.created_at).toLocaleString()}, ${
                                                    post.commentCount
                                                    } ${post.commentCount === 1 ? "reply" : "replies"}`}
                                                </Typography>
                                                </Box>
                                            </Box>
                                            <Box mt={2} display="flex" alignItems="center" gap={1}>
                                                {post.voted ? (
                                                    <Box
                                                    sx={{
                                                        position: "relative",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        "&:hover .voted-btn": {
                                                            opacity: 0,
                                                        },
                                                        "&:hover .undo-btn": {
                                                            opacity: 1,
                                                            pointerEvents: "auto",
                                                        },
                                                    }}
                                                >
                                                        <Button
                                                            size="small"
                                                            className="voted-btn"
                                                            variant="contained"
                                                            color="success"
                                                            disabled
                                                            sx={{
                                                            transition: "opacity 0.2s ease",
                                                            }}
                                                        >
                                                        Power Voted
                                                    </Button>
                                                    <Button
                                                            size="small"
                                                            className="undo-btn"
                                                            variant="contained"
                                                            color="error"
                                                            sx={{
                                                            inset: 0,
                                                            position: "absolute",
                                                            transition: "opacity 0.2s ease",
                                                            opacity: 0,
                                                            pointerEvents: "none",
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleVote(post._id, post.type);
                                                        }}
                                                    >
                                                            Undo
                                                    </Button>
                                                </Box>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleVote(post._id, post.type);}
                                                    }
                                                    >
                                                        ▲ Power Vote
                                                    </Button>
                                                )}
                                                <Typography variant="body2">{post.upvotes} Power Votes</Typography>
                                        </Box>
                                            </Box>
                                        );
                            }) 
                        )}
                                </>
                                )}
                                {postReplyView === "photos" && (
                                    <>
                                        {posts.length === 0 ? (
                                        <Typography
                                            variant="body1"
                                            sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                                        >
                                            No photos from followed users.
                                        </Typography>
                                        ) : (
                                        posts.map((post, idx) => {
                                        const isLast = idx === posts.length - 1;
                                        return (
                                        <Box
                                                key={post._id.toString()}
                                                ref={isLast ? lastPostRef : null}
                                                    onClick={() => history.push(`/photos/${post._id}/comments`)}
                                                
                                                    p={2}
                                                    my={2}
                                                    border="1px solid #ccc"
                                                    borderRadius="8px"
                                                    sx={{
                                                        position: "relative",
                                                        maxWidth: 900,
                                                        mx: "auto",
                                                        my: 2,
                                                        cursor: "pointer",
                                                        "&:hover": { backgroundColor: "#f9f9f9" },
                                                    }}
                                                    >
                                                
                                                    <Box display="flex" alignItems="flex-start" gap={1}>
                                                        <Avatar
                                                        src={post.author?.profileImageUrl || "/default-avatar.jpg"}
                                                        alt={`${post.author?.first_name || ""} ${post.author?.last_name || ""}`}
                                                        sx={{ width: 62, height: 62 }}
                                                        />
                                                        <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {post.author?.first_name && post.author?.last_name
                                                            ? `${post.author.first_name} ${post.author.last_name}`
                                                            : "Unknown Name"}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            @{post.author?.username || "unknown"}
                                                        </Typography>

                                                        {/* Photo post */}
                                                            <CardMedia
                                                            component="img"
                                                            image={post.imageUrl}
                                                            alt="User photo"
                                                            sx={{
                                                                maxWidth: 900,
                                                                width: "100%",
                                                                display: "block",
                                                                margin: "16px auto 0 auto",
                                                                borderRadius: 2,
                                                                objectFit: "contain",
                                                            }}
                                                            />

                                                            {post.description && (
                                                            <Typography
                                                                variant="body1"
                                                                sx={{ mt: 1, whiteSpace: "pre-line" }}
                                                            >
                                                                {post.description}
                                                            </Typography>
                                                            )}

                                                        <Button
                                                            size="small"
                                                            color="error"
                                                            sx={{ position: "absolute", top: 8, right: 8 }}
                                                            onClick={(e) => handleDeletePost(e, post)}
                                                        >
                                                            Delete
                                                        </Button>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: "block", mt: 0.5 }}
                                                        >
                                                            {`${new Date(post.created_at).toLocaleString()}, ${
                                                            post.commentCount
                                                            } ${post.commentCount === 1 ? "reply" : "replies"}`}
                                                        </Typography>
                                                        </Box>
                                                    </Box>
                                                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                                                        {post.voted ? (
                                                            <Box
                                                            sx={{
                                                                position: "relative",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                "&:hover .voted-btn": {
                                                                    opacity: 0,
                                                                },
                                                                "&:hover .undo-btn": {
                                                                    opacity: 1,
                                                                    pointerEvents: "auto",
                                                                },
                                                            }}
                                                        >
                                                                <Button
                                                                    size="small"
                                                                    className="voted-btn"
                                                                    variant="contained"
                                                                    color="success"
                                                                    disabled
                                                                    sx={{
                                                                    transition: "opacity 0.2s ease",
                                                                    }}
                                                                >
                                                                Power Voted
                                                            </Button>
                                                            <Button
                                                                    size="small"
                                                                    className="undo-btn"
                                                                    variant="contained"
                                                                    color="error"
                                                                    sx={{
                                                                    inset: 0,
                                                                    position: "absolute",
                                                                    transition: "opacity 0.2s ease",
                                                                    opacity: 0,
                                                                    pointerEvents: "none",
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleVote(post._id, post.type);
                                                                }}
                                                            >
                                                                    Undo
                                                            </Button>
                                                        </Box>
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleVote(post._id, post.type);}
                                                            }
                                                            >
                                                                ▲ Power Vote
                                                            </Button>
                                                        )}
                                                        <Typography variant="body2">{post.upvotes} Power Votes</Typography>
                                                </Box>
                                                    </Box>
                                              
                                                );
                            }) 
                        )}
                                    </>
                                    )}
                        </Box>
                    )}
                  {/*  {selectedTab === "Posts" && ( */}
                        {/*
                        <Box>
                        {posts.length === 0 ? (
                            <Typography>No posts from followed users.</Typography>
                        ) : (
                        // if there are posts, loop through each and render it
                        posts.map((post, idx) => (
                            <Box
                                key={idx}            // key is needed by React for list rendering
                                onClick={() => history.push(`/post/${post._id}/comments`)}
                                p={2}                // padding inside each post box
                                my={2}               // margin above and below each post box
                                border="1px solid #ccc"  // light border around each post
                                borderRadius="8px"       // rounded corners
                                sx={{
                                    position: "relative",
                                    maxWidth: 900, // limit width
                                    mx: "auto", // center horizontally
                                    my: 2, // vertical spacing between each post
                                    cursor: "pointer",
                                    "&:hover": {
                                        backgroundColor: "#f9f9f9"
                                    }
                                }}
                            >

                                <Box display="flex" alignItems="flex-start" gap={1}>
                                    <Avatar
                                        src={post.author?.profileImageUrl || '/default-avatar.jpg'}
                                        alt={`${post.author?.first_name || ''} ${post.author?.last_name || ''}`}
                                        sx={{ width: 62, height: 62 }}
                                    />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                        {post.author?.first_name && post.author?.last_name
                                            ? `${post.author.first_name} ${post.author.last_name}`
                                            : 'Unknown Name'}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                        @{post.author?.username || 'unknown'}
                                        </Typography>

                                        <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                            {post.content}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                                            {`${new Date(post.created_at).toLocaleString()}, ${post.comments.length} ${post.comments.length === 1 ? 'reply' : 'replies'}`}
                                        </Typography>

                                    </Box>
                                </Box>

                                <Box mt={2} display="flex" alignItems="center" gap={1}>
                                    <Button
                                        size="small"
                                        variant={post.voted ? "contained" : "outlined"}
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                            toggleVote(post._id)}
                                        }
                                    >
                                        ▲ {post.voted ? "Unvote" : "Upvote"}
                                    </Button>
                                    <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                    </Box>*/}
                        {/*<Typography variant="body1">
                            User's recent posts will appear here.
                        </Typography>*/}
                    {/*})}*/}
                    
                    {selectedTab === "Replies" && (
                        <Box sx={{ textAlign: "left" }}>

                            <Box display="flex" gap={2} mb={2}>
                                <Button
                                    variant={replyView === "main" ? "contained" : "outlined"}
                                    onClick={() => setReplyView("main")}
                                >
                                    Main Social
                                </Button>

                                <Button
                                    variant={replyView === "gov" ? "contained" : "outlined"}
                                    onClick={() => setReplyView("gov")}
                                >
                                    Government
                                </Button>
                            </Box>

                            {replyView === "main" && (
                            <>

                            {replies.length === 0 ? (
                            <Typography
                                variant="body1"
                                sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                            >
                                No posts from followed users.
                            </Typography>
                            ) : (
                            replies.map((post, idx) => {
                            const isLast = idx === replies.length - 1;
                            return (
                            <Box
                                key={post._id.toString()}
                                ref={isLast ? lastReplyRef : null}
                               onClick={(e) => {
                                e.stopPropagation();

                                if (!post.postId || !post.targetId) {
                                    console.error("Missing navigation IDs:", post);
                                    return;
                                }

                                switch (post.type) {
                                    case "photo-comment":
                                    case "photo-reply":
                                    history.push(`/photo/${post.postId}/comments/${post.targetId}`);
                                    break;

                                    case "video-comment":
                                    case "video-reply":
                                    history.push(`/video/${post.postId}/comments/${post.targetId}`);
                                    break;

                                    case "comment":
                                    case "reply":
                                    history.push(`/mainSocial/${post.postId}/comments/${post.targetId}`);
                                    break;

                                    default:
                                    console.warn("Unknown type:", post.type);
                                }
                                }}
                                p={2}
                                my={2}
                                border="1px solid #ccc"
                                borderRadius="8px"
                                sx={{
                                    position: "relative",
                                    maxWidth: 900,
                                    mx: "auto",
                                    my: 2,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#f9f9f9" },
                                }}
                                >
                                    {/*<Typography 
                                        variant="overline" 
                                        color="primary"
                                        //onClick={(e) => {
                                        //    e.stopPropagation(); 
                                        //    history.push(`/academicSubjects/${post.topic}`)
                                        //}}
                                        //onClick={(e) => {
                                        //    e.stopPropagation(); 
                                        //    goToTopic(post.topic, post.topicType);
                                        //}}
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                    >
                                        {post.topic}
                                    </Typography>
                                    */}
                                    {post.topic && (
                                        <Typography 
                                            variant="overline" 
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                goToTopic(post.topic, post.topicType);
                                            }}
                                            sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                            {post.topic}
                                        </Typography>
                                    )}
                                    {/* show the parent text above */}
                                    {post.parentContent  && (
                                        <Box 
                                        /*
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                                //history.push(`/post/${post.parentPostId}/comments`)
                                            // Navigation logic based on parent type
                                            if (post.parentType === "post") {
                                                // Goes to the post's main comment page
                                                history.push(`/post/${post.parentPostId}/comments`);
                                            } else {
                                                // Goes to the direct comment/reply thread
                                                history.push(`/mainSocial/${post.parentPostId}/comments/${post.parentId}`);
                                            }
                                            }
                                        }*/
                                       /*onClick={(e) => {
                                            e.stopPropagation();

                                            if (post.parentType === "photo") {
                                                history.push(`/photos/${post.postId}/comments`);
                                            } else if (post.parentType === "photo-comment") {
                                                history.push(`/photo/${post.postId}/comments/${post.commentId}`);
                                            } else if (post.parentType === "photo-reply") {
                                                history.push(`/photo/${post.postId}/comments/${post.parentId}`);
                                            } else if (post.parentType === "post") {
                                                history.push(`/post/${post.parentPostId}/comments`);
                                            } else if (post.parentType  === "video") {
                                                history.push(`/videos/${post.postId}/comments`);
                                            } else if (post.parentType  === "video-comment") {
                                                history.push(`/video/${post.postId}/comments/${post.commentId}`);
                                            }  else if (post.parentType  === "video-reply") {
                                                history.push(`/video/${post.postId}/comments/${post.parentId}`);
                                            } else {
                                                history.push(`/mainSocial/${post.parentPostId}/comments/${post.parentId}`);
                                            }
                                        }}*/
                                       onClick={(e) => {
                                        e.stopPropagation();

                                        if (!post.postId) {
                                            console.error("Missing postId:", post);
                                            return;
                                        }

                                        switch (post.parentType) {
                                            case "photo":
                                            history.push(`/photos/${post.postId}/comments`);
                                            break;

                                            case "photo-comment":
                                            case "photo-reply":
                                            history.push(`/photo/${post.postId}/comments/${post.parentId}`);
                                            break;

                                            case "video":
                                            history.push(`/videos/${post.postId}/comments`);
                                            break;

                                            case "video-comment":
                                            case "video-reply":
                                            history.push(`/video/${post.postId}/comments/${post.parentId}`);
                                            break;

                                            case "post":
                                            //history.push(`/mainSocial/${post.postId}/comments`);
                                            history.push(`/post/${post.postId}/comments`);
                                            break;

                                            case "comment":
                                            case "reply":
                                            history.push(`/mainSocial/${post.postId}/comments/${post.parentId}`);
                                            break;

                                            default:
                                            console.warn("Unknown parentType:", post.parentType);
                                        }
                                        }}
                                        mb={1} 
                                        p={1} 
                                        borderLeft="3px solid #aaa" 
                                        pl={2}
                                        sx={{
                                        "&:hover": {
                                            backgroundColor: "#d9e4f0", // inner hover overrides
                                        },
                                        }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            In reply to @{post.parentAuthorUsername}: {post.parentContent}
                                        </Typography>
                                        </Box>
                                    )}
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                    <Avatar
                                    src={post.author?.profileImageUrl || "/default-avatar.jpg"}
                                    alt={`${post.author?.first_name || ""} ${post.author?.last_name || ""}`}
                                    sx={{ width: 62, height: 62 }}
                                    />
                                    <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {post.author?.first_name && post.author?.last_name
                                        ? `${post.author.first_name} ${post.author.last_name}`
                                        : "Unknown Name"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        @{post.author?.username || "unknown"}
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        sx={{ mt: 1, whiteSpace: "pre-line" }}
                                    >
                                        {post.text}
                                    </Typography>

                                    {/*<Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        {`${new Date(post.created_at).toLocaleString()}, ${
                                        post.commentCount
                                        } ${post.commentCount === 1 ? "reply" : "replies"}`}
                                    </Typography>*/}
                                    {/*<Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        {(() => {
                                            const isReply = post.parentId !== null && post.parentId !== undefined;

                                            const count = isReply
                                            ? post.replyCount   // replies → number of replies to THIS comment
                                            : post.postCommentCount; // top-level → total comments on post

                                            return `${new Date(post.created_at).toLocaleString()}, ${count} ${
                                            count === 1 ? "reply" : "replies"
                                            }`;
                                        })()}
                                    </Typography>*/}
                                    <Button
                                        size="small"
                                        color="error"
                                        sx={{ position: "absolute", top: 8, right: 8 }}
                                        onClick={(e) => handleDeleteReply(e, post)}
                                    >
                                        Delete
                                    </Button>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        {(() => {
                                            const count = post.replyCount ?? 0;

                                            return `${new Date(post.created_at).toLocaleString()}, ${count} ${
                                            count === 1 ? "reply" : "replies"
                                            }`;
                                        })()}
                                    </Typography>
                     
                                    </Box>
                                </Box>

                                {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                    <Button
                                    size="small"
                                    variant={post.voted ? "contained" : "outlined"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (post.type.startsWith("photo")) {
                                            togglePhotoUpvotesVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        } else if (post.type.startsWith("video")) {
                                            toggleVideoUpvotesVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        } else {
                                            toggleReplyVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        }
                                    }}
                                    >
                                    ▲ {post.voted ? "Unvote" : "Upvote"}
                                    </Button>
                                    <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                </Box>*/}
                                <Box mt={2} display="flex" alignItems="center" gap={1}>
                                    {post.voted ? (
                                        <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            "&:hover .voted-btn": {
                                                opacity: 0,
                                            },
                                            "&:hover .undo-btn": {
                                                opacity: 1,
                                                pointerEvents: "auto",
                                            },
                                        }}
                                    >
                                            <Button
                                                size="small"
                                                className="voted-btn"
                                                variant="contained"
                                                color="success"
                                                disabled
                                                sx={{
                                                transition: "opacity 0.2s ease",
                                                }}
                                            >
                                            Power Voted
                                        </Button>
                                        <Button
                                                size="small"
                                                className="undo-btn"
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                inset: 0,
                                                position: "absolute",
                                                transition: "opacity 0.2s ease",
                                                opacity: 0,
                                                pointerEvents: "none",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (post.type.startsWith("photo")) {
                                                    togglePhotoUpvotesVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                } else if (post.type.startsWith("video")) {
                                                    toggleVideoUpvotesVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                } else {
                                                    toggleReplyVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                }
                                            }}
                                        >
                                                Undo
                                        </Button>
                                    </Box>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (post.type.startsWith("photo")) {
                                                    togglePhotoUpvotesVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                } else if (post.type.startsWith("video")) {
                                                    toggleVideoUpvotesVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                } else {
                                                    toggleReplyVote({
                                                        postId: post.postId,
                                                        targetId: post.targetId,
                                                        type: post.type
                                                    });
                                                }
                                            }
                                        }
                                        >
                                            ▲ Power Vote
                                        </Button>
                                    )}
                                    <Typography variant="body2">{post.upvoteCount} Power Votes</Typography>
                            </Box>
                                </Box>
                            );
      })
    )}
                        </>
                        )}
                        {replyView === "gov" && (
                            <>
                                {govComments.length === 0 ? (
                                <Typography
                                    variant="body1"
                                    sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                                >
                                    No government replies found.
                                </Typography>
                                ) : (
                                govComments.map((post, idx) => {   
                                const isLast = idx === govComments.length - 1;
                                return (
                                <Box
                                    key={post._id.toString()}
                                    ref={isLast ? lastGovReplyRef : null}
                                    p={2}
                                    my={2}
                                    border="1px solid #ccc"
                                    borderRadius="8px"
                                    //onClick={() =>
                                    //    history.push(`/gov/${post.govType}/${post.govId}/comments/${post._id}`)
                                    //}
                                    /*onClick={(e) => {
                                        e.stopPropagation();
                                        if (post.govType == "bill") {
                                            history.push(`/bill/${post.govId}/comments/${post._id}`)
                                        } else if (post.govType == "scotus") {
                                            history.push(`/scotusCase/${post.govId}/comments/${post._id}`)
                                        } else if (post.govType == "stateBill") {
                                            history.push(`/calBill/${post.govId}/comments/${post._id}`)
                                        }
                                        }
                                    }*/
                                   onClick={(e) => {
                                        e.stopPropagation();

                                        if (!post.govId || !post.targetId) {
                                            console.error("Missing IDs:", post);
                                            return;
                                        }

                                        switch (post.govType) {
                                            case "congressionalBill":
                                                history.push(`/bill/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "scotus":
                                                history.push(`/scotusCase/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "stateBill":
                                                history.push(`/calBill/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "houseOfRepElection":
                                                history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments/${post.targetId}`)
                                                break;
                                            case "senateElection":
                                                history.push(`/Government/Elections/USSenateElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "gubernatorialElection":
                                                history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "stateLegislatureElection":
                                                history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "statewideOfficesElection":
                                                history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "govPost":
                                                history.push(`/govPost/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            default:
                                                console.warn("Unknown govType:", post.govType);
                                        }
                                    }}
                                    sx={{
                                        position: "relative",
                                        maxWidth: 900,
                                        mx: "auto",
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "#f9f9f9" },
                                    }}
                                    >
                                    <Box
                                        mb={1}
                                        p={1}
                                        borderLeft="3px solid #aaa"
                                        pl={2}
                                        /*onClick={(e) => {
                                            e.stopPropagation();
                                            if (post.govType == "bill") {
                                                history.push(`/bill/${post.govId}/comments`)
                                            } else if (post.govType == "scotus") {
                                                history.push(`/scotusCase/${post.govId}/comments`)
                                            } else if (post.govType == "stateBill") {
                                                history.push(`/calBill/${post.govId}/comments`)
                                            }
                                          }
                                        }*/
                                       onClick={(e) => {
                                            e.stopPropagation();

                                            if (!post.govId) return;

                                            switch (post.govType) {
                                                case "congressionalBill":
                                                    history.push(`/bill/${post.govId}/comments`);
                                                    break;
                                                case "scotus":
                                                    history.push(`/scotusCase/${post.govId}/comments`);
                                                    break;
                                                case "stateBill":
                                                    history.push(`/calBill/${post.govId}/comments`);
                                                    break;
                                                case "houseOfRepElection":
                                                    history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments`);
                                                    break;
                                                case "senateElection":
                                                    history.push(`/Government/Elections/USSenateElections/${post.govId}/comments`);
                                                    break;
                                                case "gubernatorialElection":
                                                    history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments`);
                                                    break;
                                                case "stateLegislatureElection":
                                                    history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments`);
                                                    break;
                                                case "statewideOfficesElection":
                                                    history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments`);
                                                    break;
                                                case "govPost":
                                                    history.push(`/govPost/${post.govId}/comments`);
                                                    break;
                                            }
                                        }}
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            {post.govTitle}
                                        </Typography>
                                    </Box>
                                    {/* Parent context (replying to) */}
                                    {post.parentId && post.parentContent && (
                                        <Box
                                        mb={1}
                                        p={1}
                                        borderLeft="3px solid #aaa"
                                        pl={2}
                                        //onClick={(e) => e.stopPropagation()}
                                        /*onClick={(e) => {
                                        e.stopPropagation();
                                            if (post.govType == "bill") {
                                                history.push(`/bill/${post.govId}/comments/${post.parentId}`);
                                            } else if (post.govType == "scotus") {
                                                history.push(`/scotusCase/${post.govId}/comments/${post.parentId}`);
                                            } else if (post.govType == "stateBill") {
                                                history.push(`/calBill/${post.govId}/comments/${post.parentId}`);
                                            }
                                        }}*/
                                       onClick={(e) => {
                                            e.stopPropagation();

                                            if (!post.govId || !post.parentId) return;

                                            switch (post.govType) {
                                                case "congressionalBill":
                                                    history.push(`/bill/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "scotus":
                                                    history.push(`/scotusCase/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "stateBill":
                                                    history.push(`/calBill/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "houseOfRepElection":
                                                    history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments/${post.parentId}`)
                                                    break;
                                                case "senateElection":
                                                    history.push(`/Government/Elections/USSenateElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "gubernatorialElection":
                                                    history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "stateLegislatureElection":
                                                    history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "statewideOfficesElection":
                                                    history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "govPost":
                                                    history.push(`/govPost/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                            }
                                        }}
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            In reply to @{post.parentAuthorUsername}: {post.parentContent}
                                        </Typography>
                                        </Box>
                                    )}

                                    {/* Main content */}
                                    <Box display="flex" gap={1}>
                                        <Avatar
                                        src={post.author.profileImageUrl || "/default-avatar.jpg"}
                                        sx={{ width: 62, height: 62 }}
                                        />

                                        <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {post.author.first_name && post.author.last_name
                                            ? `${post.author.first_name} ${post.author.last_name}`
                                            : "Unknown Name"}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary">
                                            @{post.author.username || "unknown"}
                                        </Typography>

                                        <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                            {post.text}
                                        </Typography>

                                        <Button
                                            size="small"
                                            color="error"
                                            sx={{ position: "absolute", top: 8, right: 8 }}
                                            onClick={(e) => handleDeleteGovComment(e, post)}
                                        >
                                            Delete
                                        </Button>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                        >
                                            {new Date(post.created_at).toLocaleString()},{" "}
                                            {post.replyCount}{" "}
                                            {post.replyCount=== 1 ? "reply" : "replies"}
                                        </Typography>
                                        </Box>
                                    </Box>

                                    {/* Upvote bar */}
                                    {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                        <Button
                                        size="small"
                                        variant={post.voted ? "contained" : "outlined"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const isTopLevel = !post.parentId;
                                            if (post.govType == "bill" && isTopLevel) {
                                                toggleBillCommentVote(post.commentId);
                                            } else if (post.govType == "bill" && !isTopLevel) {
                                                toggleBillReplyVote(post.commentId, post.rootCommentId);
                                            } else if (post.govType == "scotus" && isTopLevel) {
                                                toggleScotusCommentVote(post.commentId);
                                            } else if (post.govType == "scotus" && !isTopLevel) {
                                                toggleScotusReplyVote(post.commentId, post.rootCommentId);
                                            } else if (post.govType == "stateBill" && isTopLevel) {
                                                toggleStateBillCommentVote(post.commentId);
                                            } else if (post.govType == "stateBill" && !isTopLevel) {
                                                toggleStateBillReplyVote(post.commentId, post.rootCommentId);
                                            }
                                         }
                                        }
                                        >
                                        ▲ {post.voted ? "Unvote" : "Upvote"}
                                        </Button>

                                        <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                    </Box>*/}
                                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                                        {post.voted ? (
                                            <Box
                                            sx={{
                                                position: "relative",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                "&:hover .voted-btn": {
                                                    opacity: 0,
                                                },
                                                "&:hover .undo-btn": {
                                                    opacity: 1,
                                                    pointerEvents: "auto",
                                                },
                                            }}
                                        >
                                                <Button
                                                    size="small"
                                                    className="voted-btn"
                                                    variant="contained"
                                                    color="success"
                                                    disabled
                                                    sx={{
                                                    transition: "opacity 0.2s ease",
                                                    }}
                                                >
                                                Power Voted
                                            </Button>
                                            <Button
                                                    size="small"
                                                    className="undo-btn"
                                                    variant="contained"
                                                    color="error"
                                                    sx={{
                                                    inset: 0,
                                                    position: "absolute",
                                                    transition: "opacity 0.2s ease",
                                                    opacity: 0,
                                                    pointerEvents: "none",
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleGovVote({
                                                        govType: post.govType,
                                                        govId: post.govId,
                                                        targetId: post.targetId,
                                                    });
                                                }}
                                            >
                                                    Undo
                                            </Button>
                                        </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleGovVote({
                                                        govType: post.govType,
                                                        govId: post.govId,
                                                        targetId: post.targetId,
                                                    });
                                                }}
                                            >
                                                ▲ Power Vote
                                            </Button>
                                        )}
                                        <Typography variant="body2">{post.upvoteCount} Power Votes</Typography>
                                </Box>
                                    </Box>
                                );
                            })
                            )}
                            </>
                            )}

                        </Box>
                    )}
                    
                    {selectedTab === "Power Votes" && (
                        <Box sx={{ textAlign: "left" }}>

                            <Box display="flex" gap={2} mb={2}>
                                <Button
                                    variant={replyViewUpvotes === "main" ? "contained" : "outlined"}
                                    onClick={() => setReplyViewUpvotes("main")}
                                >
                                    Main Social
                                </Button>

                                <Button
                                    variant={replyViewUpvotes === "gov" ? "contained" : "outlined"}
                                    onClick={() => setReplyViewUpvotes("gov")}
                                >
                                    Government
                                </Button>
                            </Box>

                            {replyViewUpvotes === "main" && (
                            <>
                                {upvotedReplies.map((post, idx) => {
                                const isLast = idx === upvotedReplies.length - 1;
                                return (
                                <Box
                                    key={post._id.toString()}
                                    ref={isLast ? lastUpvotedRef : null}
                                /*onClick={(e) => {
                                    e.stopPropagation();

                                    if (post.type === "post") {
                                        history.push(`/post/${post.postId}/comments`);
                                    } 
                                    else if (post.type === "comment") {
                                        history.push(`/mainSocial/${post.postId}/comments/${post.commentId}`);
                                    } 
                                    else if (post.type === "reply") {
                                        history.push(`/mainSocial/${post.postId}/comments/${post.replyId}`);
                                    } else if (post.type === "photo") {
                                        history.push(`/photos/${post.postId}/comments`);
                                    } else if (post.type === "video") {
                                        history.push(`/videos/${post.postId}/comments`);
                                    } else if (post.type === "photo-comment") {
                                        history.push(`/photo/${post.postId}/comments/${post.commentId}`);
                                    } else if (post.type === "video-comment") {
                                        history.push(`/video/${post.postId}/comments/${post.commentId}`);
                                    } else if (post.type === "photo-reply") {
                                        history.push(`/photo/${post.postId}/comments/${post.replyId}`);
                                    } else if (post.type === "video-reply") {
                                        history.push(`/video/${post.postId}/comments/${post.replyId}`);
                                    }
                                }}*/
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigateToUpvote(post);
                                }}
                                p={2}
                                my={2}
                                border="1px solid #ccc"
                                borderRadius="8px"
                                sx={{
                                    position: "relative",
                                    maxWidth: 900,
                                    mx: "auto",
                                    my: 2,
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: "#f9f9f9" },
                                }}
                                >
                                    {post.topic && (
                                        <Typography 
                                            variant="overline" 
                                            color="primary"
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                goToTopic(post.topic, post.topicType);
                                            }}
                                            sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                            {post.topic}
                                        </Typography>
                                    )}
                                {/* show the parent text above */}
                                    {!(post.type === "post" || post.type === "photo" || post.type === "video") && post.parentContent && (
                                        <Box 
                                        /*
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                                //history.push(`/post/${post.parentPostId}/comments`)
                                            // Navigation logic based on parent type
                                            if (post.parentType === "post") {
                                                // Goes to the post's main comment page
                                                history.push(`/post/${post.postId}/comments`);
                                            } else {
                                                // Goes to the direct comment/reply thread
                                                history.push(`/mainSocial/${post.postId}/comments/${post.parentId}`);
                                            }
                                            }
                                        }*/
                                       /*
                                       onClick={(e) => {
                                        e.stopPropagation();
                                        const type = post.parentType;  
                                        // --- POSTS ---
                                        if (type === "post") {
                                            history.push(`/post/${post.postId}/comments`);
                                        }
                                        // --- COMMENTS ---
                                        else if (type === "comment") {
                                            history.push(`/mainSocial/${post.postId}/comments/${post.parentId}`);
                                        }
                                        // --- REPLIES ---
                                        else if (type === "reply") {
                                            history.push(`/mainSocial/${post.postId}/comments/${post.parentId}`);
                                        }
                                        // --- PHOTO POSTS ---
                                        else if (type === "photo") {
                                            history.push(`/photos/${post.postId}/comments`);
                                        }
                                        // --- PHOTO COMMENTS ---
                                        else if (type === "photo-comment") {
                                            history.push(`/photo/${post.postId}/comments/${post.parentId}`);
                                        }
                                        // --- PHOTO REPLIES ---
                                        else if (type === "photo-reply") {
                                            history.push(`/photo/${post.postId}/comments/${post.parentId}`);
                                        }
                                        // --- VIDEO POSTS ---
                                        else if (type === "video") {
                                            history.push(`/videos/${post.postId}/comments`);
                                        }
                                        // --- VIDEO COMMENTS ---
                                        else if (type === "video-comment") {
                                            history.push(`/video/${post.postId}/comments/${post.parentId}`);
                                        }
                                        // --- VIDEO REPLIES ---
                                        else if (type === "video-reply") {
                                            history.push(`/video/${post.postId}/comments/${post.parentId}`);
                                        }
                                    }}*/
                                   /*onClick={(e) => {
                                    e.stopPropagation();

                                    const type = post.parentType;
                                    const rootId = post.postId;      // id of the post/photo/video
                                    const parentId = post.parentId;  // id of parent comment/reply (if any)

                                    // defensive: if rootId is missing, don't navigate
                                    if (!rootId) return;

                                    // map parent types to the correct route
                                    switch (type) {
                                        // MAIN POSTS
                                        case "post":
                                        // go to the post's top-level comments page
                                        history.push(`/post/${rootId}/comments`);
                                        break;

                                        // MAIN SOCIAL comments/replies (comment thread)
                                        case "comment":
                                        case "reply":
                                        // go to the specific comment/reply thread
                                        if (parentId) {
                                            history.push(`/mainSocial/${rootId}/comments/${parentId}`);
                                        } else {
                                            history.push(`/mainSocial/${rootId}/comments`);
                                        }
                                        break;

                                        // PHOTO top-level post
                                        case "photo":
                                        history.push(`/photos/${rootId}/comments`);
                                        break;

                                        // PHOTO comment / reply thread
                                        case "photo-comment":
                                        case "photo-reply":
                                        if (parentId) {
                                            history.push(`/photo/${rootId}/comments/${parentId}`);
                                        } else {
                                            history.push(`/photo/${rootId}/comments`);
                                        }
                                        break;

                                        // VIDEO top-level post
                                        case "video":
                                        history.push(`/videos/${rootId}/comments`);
                                        break;

                                        // VIDEO comment / reply thread
                                        case "video-comment":
                                        case "video-reply":
                                        if (parentId) {
                                            history.push(`/video/${rootId}/comments/${parentId}`);
                                        } else {
                                            history.push(`/video/${rootId}/comments`);
                                        }
                                        break;

                                        // fallback - safe default
                                        default:
                                        history.push(`/post/${rootId}/comments`);
                                    }
                                    }}*/
                                   onClick={(e) => {
                                            e.stopPropagation();

                                            if (!post.postId) return;

                                            // ONLY navigate if parentId exists
                                            if (!post.parentId) {
                                                return history.push(`/post/${post.postId}/comments`);
                                            }

                                            switch (post.type) {
                                                case "comment":
                                                case "reply":
                                                    history.push(`/mainSocial/${post.postId}/comments/${post.parentId}`);
                                                    break;

                                                case "photo-comment":
                                                case "photo-reply":
                                                    history.push(`/photo/${post.postId}/comments/${post.parentId}`);
                                                    break;

                                                case "video-comment":
                                                case "video-reply":
                                                    history.push(`/video/${post.postId}/comments/${post.parentId}`);
                                                    break;

                                                default:
                                                    history.push(`/post/${post.postId}/comments`);
                                            }
                                        }}
                                        mb={1} 
                                        p={1} 
                                        borderLeft="3px solid #aaa" 
                                        pl={2}
                                        sx={{
                                        "&:hover": {
                                            backgroundColor: "#d9e4f0", // inner hover overrides
                                        },
                                        }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            In reply to @{post.parentAuthorUsername}: {post.parentContent}
                                        </Typography>
                                        </Box>
                                    )}
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                    <Avatar
                                    src={post.author?.profileImageUrl || "/default-avatar.jpg"}
                                    alt={`${post.author?.first_name || ""} ${post.author?.last_name || ""}`}
                                    onClick={(e) => visitProfile(e, post.author?._id)}
                                    sx={{ 
                                        width: 62, 
                                        height: 62,
                                        cursor: "pointer",
                                        "&:hover": {
                                            backgroundColor: "#d9e4f0",
                                        }, 
                                        }}
                                    />
                                    <Box>
                                    <Typography variant="subtitle1" fontWeight="bold"
                                    onClick={(e) => visitProfile(e, post.author?._id)}
                                    sx={{
                                        cursor: "pointer",
                                        display: "inline-block",
                                        "&:hover": {
                                        textDecoration: "underline",
                                        textDecorationColor: "currentColor", 
                                        textUnderlineOffset: "3px",
                                        },
                                    }}
                                    >
                                        {post.author?.first_name && post.author?.last_name
                                        ? `${post.author.first_name} ${post.author.last_name}`
                                        : "Unknown Name"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary"
                                    onClick={(e) => visitProfile(e, post.author?._id)}
                                    sx={{
                                        cursor: "pointer",
                                        display: "block",
                                        "&:hover": {
                                        textDecoration: "underline",
                                        textDecorationColor: "#currentColor",
                                        textUnderlineOffset: "3px",
                                        },
                                    }}
                                    >
                                        @{post.author?.username || "unknown"}
                                    </Typography>

                                    {post.type !== "photo" && post.type !== "video" && (
                                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                        {post.text || post.content || "[no text]"}
                                    </Typography>
                                    )}
                                    {/*
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", mt: 0.5 }}
                                    >
                                        {`${new Date(post.created_at).toLocaleString()}, ${
                                        post.comments?.length
                                        } ${post.comments?.length === 1 ? "reply" : "replies"}`}
                                    </Typography> */}

                                    {post.type === "photo" && (
                                    <>
                                        <CardMedia
                                        component="img"
                                        image={post.imageUrl}
                                        alt="User photo"
                                        sx={{
                                            maxWidth: 900,
                                            width: "100%",
                                            display: "block",
                                            margin: "16px auto 0 auto",
                                            borderRadius: 2,
                                            objectFit: "contain",
                                        }}
                                        />

                                        {post.description && (
                                        <Typography
                                            variant="body1"
                                            sx={{ mt: 1, whiteSpace: "pre-line" }}
                                        >
                                            {post.description}
                                        </Typography>
                                        )}
                                    </>
                                    )}

                                    {/*{post.type === "video" && (
                                        <>
                                        <Typography variant="subtitle1">{post.title}</Typography>
                                        <CardMedia
                                            component="video"
                                            controls
                                            src={`http://localhost:3000${post.videoUrl}`}
                                            sx={{
                                            maxWidth: 900,
                                            width: "100%",
                                            display: "block",
                                            margin: "16px auto 0 auto",
                                            borderRadius: 2,
                                            objectFit: "contain",
                                            }}
                                        />
                                        <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                            {post.content}
                                        </Typography>
                                        </>
                                    )}*/}
                                    {post.type === "video" && (
                                        <>
                                        <Typography variant="subtitle1">{post.title}</Typography>
                                        <CardMedia
                                            component="img"
                                            controls
                                            src={post.thumbnailUrl}
                                            sx={{
                                            maxWidth: 900,
                                            width: "100%",
                                            display: "block",
                                            margin: "16px auto 0 auto",
                                            borderRadius: 2,
                                            objectFit: "contain",
                                            }}
                                        />
                                        </>
                                    )}

                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(post.created_at).toLocaleString()},{" "}

                                        {post.type === "post" && (
                                            <>
                                            {post.commentCount ?? 0}{" "}
                                            {(post.commentCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "comment" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "reply" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "photo" && (
                                            <>
                                            {post.commentCount ?? 0}{" "}
                                            {(post.commentCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "photo-comment" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "photo-reply" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}


                                        {post.type === "video" && (
                                            <>
                                            {post.commentCount ?? 0}{" "}
                                            {(post.commentCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "video-comment" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}

                                        {post.type === "video-reply" && (
                                            <>
                                            {post.replyCount ?? 0}{" "}
                                            {(post.replyCount ?? 0) === 1 ? "reply" : "replies"}
                                            </>
                                        )}
                                    </Typography>

                                    </Box>
                                </Box>

                                {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                    <Button
                                    size="small"
                                    variant={post.voted ? "contained" : "outlined"}
        
                                   onClick={(e) => {
                                        e.stopPropagation();
                                        if (post.type.startsWith("photo")) {
                                            togglePhotoUpvotesVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        } else if (post.type.startsWith("video")) {
                                            toggleVideoUpvotesVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        } else {
                                            toggleUpvotesVote({
                                                postId: post.postId,
                                                commentId: post.commentId,
                                                replyId: post.replyId,
                                                type: post.type
                                            });
                                        }
                                    }}
                                    >
                                    ▲ {post.voted ? "Unvote" : "Upvote"}
                                    </Button>
                                    <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                </Box>*/}
                                <Box mt={2} display="flex" alignItems="center" gap={1}>
                                        {post.voted ? (
                                            <Box
                                            sx={{
                                                position: "relative",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                "&:hover .voted-btn": {
                                                    opacity: 0,
                                                },
                                                "&:hover .undo-btn": {
                                                    opacity: 1,
                                                    pointerEvents: "auto",
                                                },
                                            }}
                                        >
                                                <Button
                                                    size="small"
                                                    className="voted-btn"
                                                    variant="contained"
                                                    color="success"
                                                    disabled
                                                    sx={{
                                                    transition: "opacity 0.2s ease",
                                                    }}
                                                >
                                                Power Voted
                                            </Button>
                                            <Button
                                                    size="small"
                                                    className="undo-btn"
                                                    variant="contained"
                                                    color="error"
                                                    sx={{
                                                    inset: 0,
                                                    position: "absolute",
                                                    transition: "opacity 0.2s ease",
                                                    opacity: 0,
                                                    pointerEvents: "none",
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (post.type.startsWith("photo")) {
                                                        togglePhotoUpvotesVote({
                                                            postId: post.postId,
                                                            targetId: post.targetId,
                                                            type: post.type
                                                        });
                                                    } else if (post.type.startsWith("video")) {
                                                        toggleVideoUpvotesVote({
                                                            postId: post.postId,
                                                            targetId: post.targetId,
                                                            type: post.type
                                                        });
                                                    } else {
                                                        toggleUpvotesVote({
                                                            postId: post.postId,
                                                            commentId: post.commentId,
                                                            replyId: post.replyId,
                                                            type: post.type
                                                        });
                                                    }
                                                }}
                                            >
                                                    Undo
                                            </Button>
                                        </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (post.type.startsWith("photo")) {
                                                        togglePhotoUpvotesVote({
                                                            postId: post.postId,
                                                            targetId: post.targetId,
                                                            type: post.type
                                                        });
                                                    } else if (post.type.startsWith("video")) {
                                                        toggleVideoUpvotesVote({
                                                            postId: post.postId,
                                                            targetId: post.targetId,
                                                            type: post.type
                                                        });
                                                    } else {
                                                        toggleUpvotesVote({
                                                            postId: post.postId,
                                                            commentId: post.commentId,
                                                            replyId: post.replyId,
                                                            type: post.type
                                                        });
                                                    }
                                                }}
                                            >
                                                ▲ Power Vote
                                            </Button>
                                        )}
                                        <Typography variant="body2">{post.upvoteCount} Power Votes</Typography>
                                </Box>
                                </Box>
                            );
                            })}
                            </>
                            )}
                            {replyViewUpvotes === "gov" && (
                            <>
                                {govUpvotedReplies.length === 0 ? (
                                <Typography
                                    variant="body1"
                                    sx={{ textAlign: "center", color: "text.secondary", mt: 2 }}
                                >
                                    No government replies found.
                                </Typography>
                                ) : (
                                govUpvotedReplies.map((post, idx) => {  
                                const isLast = idx === govUpvotedReplies.length - 1;
                                return (
                                <Box
                                    key={post._id.toString()}
                                    ref={isLast ? lastGovUpvotedRef : null}
                                    p={2}
                                    my={2}
                                    border="1px solid #ccc"
                                    borderRadius="8px"
                                    //onClick={() =>
                                    //    history.push(`/gov/${post.govType}/${post.govId}/comments/${post._id}`)
                                    //}
                                    onClick={(e) => {
                                        e.stopPropagation();

                                        if (!post.govId || !post.targetId) {
                                            console.error("Missing IDs:", post);
                                            return;
                                        }

                                        switch (post.govType) {
                                            case "congressionalBill":
                                                history.push(`/bill/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "scotus":
                                                history.push(`/scotusCase/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "stateBill":
                                                history.push(`/calBill/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "houseOfRepElection":
                                                history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments/${post.targetId}`)
                                                break;
                                            case "senateElection":
                                                history.push(`/Government/Elections/USSenateElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "gubernatorialElection":
                                                history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "stateLegislatureElection":
                                                history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "statewideOfficesElection":
                                                history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            case "govPost":
                                                history.push(`/govPost/${post.govId}/comments/${post.targetId}`);
                                                break;
                                            default:
                                                console.warn("Unknown govType:", post.govType);
                                        }
                                    }}
                                    sx={{
                                        position: "relative",
                                        maxWidth: 900,
                                        mx: "auto",
                                        cursor: "pointer",
                                        "&:hover": { backgroundColor: "#f9f9f9" },
                                    }}
                                    >
                                    {/*<Box
                                        mb={1}
                                        p={1}
                                        borderLeft="3px solid #aaa"
                                        pl={2}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (post.govType == "bill") {
                                                history.push(`/bill/${post.govId}/comments`)
                                            } else if (post.govType == "scotus") {
                                                history.push(`/scotusCase/${post.govId}/comments`)
                                            } else if (post.govType == "stateBill") {
                                                history.push(`/calBill/${post.govId}/comments`)
                                            }
                                          }
                                        }
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            {post.govTitle}
                                        </Typography>
                                    </Box>*/}
                                    <Box
                                        mb={1}
                                        p={1}
                                        borderLeft="3px solid #aaa"
                                        pl={2}
                                        /*onClick={(e) => {
                                            e.stopPropagation();
                                            if (post.govType == "bill") {
                                                history.push(`/bill/${post.govId}/comments`)
                                            } else if (post.govType == "scotus") {
                                                history.push(`/scotusCase/${post.govId}/comments`)
                                            } else if (post.govType == "stateBill") {
                                                history.push(`/calBill/${post.govId}/comments`)
                                            }
                                          }
                                        }*/
                                       onClick={(e) => {
                                            e.stopPropagation();

                                            if (!post.govId) return;

                                            switch (post.govType) {
                                                case "congressionalBill":
                                                    history.push(`/bill/${post.govId}/comments`);
                                                    break;
                                                case "scotus":
                                                    history.push(`/scotusCase/${post.govId}/comments`);
                                                    break;
                                                case "stateBill":
                                                    history.push(`/calBill/${post.govId}/comments`);
                                                    break;
                                                case "houseOfRepElection":
                                                    history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments`);
                                                    break;
                                                case "senateElection":
                                                    history.push(`/Government/Elections/USSenateElections/${post.govId}/comments`);
                                                    break;
                                                case "gubernatorialElection":
                                                    history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments`);
                                                    break;
                                                case "stateLegislatureElection":
                                                    history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments`);
                                                    break;
                                                case "statewideOfficesElection":
                                                    history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments`);
                                                    break;
                                                case "govPost":
                                                    history.push(`/govPost/${post.govId}/comments`);
                                                    break;
                                            }
                                        }}
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            {post.govTitle}
                                        </Typography>
                                    </Box>
                                    {/* Parent context (replying to) */}
                                    {post.type === "reply" && post.parentContent && (
                                        <Box
                                        mb={1}
                                        p={1}
                                        borderLeft="3px solid #aaa"
                                        pl={2}
                                        //onClick={(e) => e.stopPropagation()}
                                        /*onClick={(e) => {
                                        e.stopPropagation();
                                            if (post.govType == "bill") {
                                                history.push(`/bill/${post.govId}/comments/${post.parentId}`);
                                            } else if (post.govType == "scotus") {
                                                history.push(`/scotusCase/${post.govId}/comments/${post.parentId}`);
                                            } else if (post.govType == "stateBill") {
                                                history.push(`/calBill/${post.govId}/comments/${post.parentId}`);
                                            }
                                        }}*/
                                       onClick={(e) => {
                                            e.stopPropagation();

                                            if (!post.govId || !post.parentId) return;

                                            switch (post.govType) {
                                                case "congressionalBill":
                                                    history.push(`/bill/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "scotus":
                                                    history.push(`/scotusCase/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "stateBill":
                                                    history.push(`/calBill/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "houseOfRepElection":
                                                    history.push(`/Government/Elections/USHouseOfRepElections/${post.govId}/comments/${post.parentId}`)
                                                    break;
                                                case "senateElection":
                                                    history.push(`/Government/Elections/USSenateElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "gubernatorialElection":
                                                    history.push(`/Government/Elections/GubernatorialElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "stateLegislatureElection":
                                                    history.push(`/Government/Elections/StateLegislatureElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "statewideOfficesElection":
                                                    history.push(`/Government/Elections/StatewideOfficesElections/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                                case "govPost":
                                                    history.push(`/govPost/${post.govId}/comments/${post.parentId}`);
                                                    break;
                                            }
                                        }}
                                        sx={{ "&:hover": { backgroundColor: "#d9e4f0" } }}
                                        >
                                        <Typography variant="caption" color="textSecondary">
                                            In reply to @{post.parentAuthorUsername}: {post.parentContent}
                                        </Typography>
                                        </Box>
                                    )}

                                    {/* Main content */}
                                    <Box display="flex" gap={1}>
                                        <Avatar
                                        src={post.author.profileImageUrl || "/default-avatar.jpg"}
                                        onClick={(e) => visitProfile(e, post.author?._id)}
                                        sx={{ 
                                            width: 62, 
                                            height: 62,
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: "#d9e4f0",
                                            }, 
                                            }}
                                        />

                                        <Box>
                                        <Typography variant="subtitle1" fontWeight="bold"
                                        onClick={(e) => visitProfile(e, post.author?._id)}
                                        sx={{
                                            cursor: "pointer",
                                            display: "inline-block",
                                            "&:hover": {
                                            textDecoration: "underline",
                                            textDecorationColor: "currentColor", 
                                            textUnderlineOffset: "3px",
                                            },
                                        }}
                                        >
                                            {post.author.first_name && post.author.last_name
                                            ? `${post.author.first_name} ${post.author.last_name}`
                                            : "Unknown Name"}
                                        </Typography>

                                        <Typography variant="body2" color="text.secondary"
                                        onClick={(e) => visitProfile(e, post.author?._id)}
                                        sx={{
                                            cursor: "pointer",
                                            display: "block",
                                            "&:hover": {
                                            textDecoration: "underline",
                                            textDecorationColor: "#currentColor",
                                            textUnderlineOffset: "3px",
                                            },
                                        }}
                                        >
                                            @{post.author.username || "unknown"}
                                        </Typography>

                                        <Typography variant="body1" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                            {post.text}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{ display: "block", mt: 0.5 }}
                                        >
                                            {new Date(post.created_at).toLocaleString()},{" "}
                                            {post.replyCount}{" "}
                                            {post.replyCount === 1 ? "reply" : "replies"}
                                        </Typography>
                                        </Box>
                                    </Box>

                                    {/* Upvote bar */}
                                    {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                        <Button
                                        size="small"
                                        variant={post.voted ? "contained" : "outlined"}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const isTopLevel = !post.parentId;
                                            if (post.govType == "bill" && isTopLevel) {
                                                toggleBillCommentVote(post.commentId);
                                            } else if (post.govType == "bill" && !isTopLevel) {
                                                toggleBillReplyVote(post.commentId, post.rootCommentId);
                                            } else if (post.govType == "scotus" && isTopLevel) {
                                                toggleScotusCommentVote(post.commentId);
                                            } else if (post.govType == "scotus" && !isTopLevel) {
                                                toggleScotusReplyVote(post.commentId, post.rootCommentId);
                                            } else if (post.govType == "stateBill" && isTopLevel) {
                                                toggleStateBillCommentVote(post.commentId);
                                            } else if (post.govType == "stateBill" && !isTopLevel) {
                                                toggleStateBillReplyVote(post.commentId, post.rootCommentId);
                                            }
                                         }
                                        }
                                        >
                                        ▲ {post.voted ? "Unvote" : "Upvote"}
                                        </Button>

                                        <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                    </Box>*/}
                                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                                        {post.voted ? (
                                            <Box
                                            sx={{
                                                position: "relative",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                "&:hover .voted-btn": {
                                                    opacity: 0,
                                                },
                                                "&:hover .undo-btn": {
                                                    opacity: 1,
                                                    pointerEvents: "auto",
                                                },
                                            }}
                                        >
                                                <Button
                                                    size="small"
                                                    className="voted-btn"
                                                    variant="contained"
                                                    color="success"
                                                    disabled
                                                    sx={{
                                                    transition: "opacity 0.2s ease",
                                                    }}
                                                >
                                                Power Voted
                                            </Button>
                                            <Button
                                                    size="small"
                                                    className="undo-btn"
                                                    variant="contained"
                                                    color="error"
                                                    sx={{
                                                    inset: 0,
                                                    position: "absolute",
                                                    transition: "opacity 0.2s ease",
                                                    opacity: 0,
                                                    pointerEvents: "none",
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleGovVote({
                                                        govType: post.govType,
                                                        govId: post.govId,
                                                        targetId: post.targetId,
                                                    });
                                                }}
                                            >
                                                    Undo
                                            </Button>
                                        </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleGovVote({
                                                        govType: post.govType,
                                                        govId: post.govId,
                                                        targetId: post.targetId,
                                                    });
                                                }}
                                            >
                                                ▲ Power Vote
                                            </Button>
                                        )}
                                        <Typography variant="body2">{post.upvoteCount} Power Votes</Typography>
                                </Box>
                                    </Box>
                                );
                            })
                            )}
                            </>
                            )}
                        </Box>
                    )}
                    {selectedTab === "Blog" && (
                        <Box sx={{ 
                                textAlign: "left", 
                                }}
                        >
                            <Box display="flex" gap={2} mb={2}>
                                <Button
                                    variant={sortType === "latest" ? "contained" : "outlined"}
                                    onClick={() => setSortType("latest")}
                                >
                                    Latest
                                </Button>

                                <Button
                                    variant={sortType === "top" ? "contained" : "outlined"}
                                    onClick={() => setSortType("top")}
                                >
                                    Top
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => setOpenBlogDialog(true)}
                                >
                                    Post
                                </Button>
                            </Box>
                        {!selectedPost && (
                            <Box>
                            {/* Blog Posts List */}
                            {blogPosts.length === 0 ? (
                                <Typography variant="body1">No blog posts yet.</Typography>
                            ) : (
                                sortedPosts.map((post) => (
                                    <Box
                                        key={post._id}
                                        onClick={() => setSelectedPost(post)}
                                        p={2}
                                        //sx={{ 
                                        //    border: "1px solid #ccc",
                                        //    borderRadius: 2,
                                        //    mb: 2,
                                        //    cursor: "pointer"
                                        //}}
                                        sx={{
                                            border: "1px solid #ccc",
                                            borderRadius: 2,
                                            mb: 2,
                                            position: "relative",
                                            maxWidth: 900,
                                            mx: "auto",
                                            cursor: "pointer",
                                            "&:hover": { backgroundColor: "#f9f9f9" },
                                        }}
                                    >
                                        <Typography variant="h6">{post.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(post.created_at).toLocaleString()}
                                        </Typography>

                                        {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                            <Button
                                                size="small"
                                                variant={post.voted ? "contained" : "outlined"}
                                                onClick={async (e) => {
                                                e.stopPropagation(); // prevents clicking the post
                                                toggleBlogVote(post._id);
                                            }}
                                            >
                                                ▲ {post.voted ? "Unvote" : "Upvote"}
                                            </Button>
                                        <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                    </Box>*/}
                                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                                    {post.voted ? (
                                        <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            "&:hover .voted-btn": {
                                                opacity: 0,
                                            },
                                            "&:hover .undo-btn": {
                                                opacity: 1,
                                                pointerEvents: "auto",
                                            },
                                        }}
                                    >
                                            <Button
                                                size="small"
                                                className="voted-btn"
                                                variant="contained"
                                                color="success"
                                                disabled
                                                sx={{
                                                transition: "opacity 0.2s ease",
                                                }}
                                            >
                                            Power Voted
                                        </Button>
                                        <Button
                                                size="small"
                                                className="undo-btn"
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                inset: 0,
                                                position: "absolute",
                                                transition: "opacity 0.2s ease",
                                                opacity: 0,
                                                pointerEvents: "none",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleBlogVote(post._id, post.type);
                                            }}
                                        >
                                                Undo
                                        </Button>
                                    </Box>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            toggleBlogVote(post._id, post.type);}
                                        }
                                        >
                                            ▲ Power Vote
                                        </Button>
                                    )}
                                    <Typography variant="body2">{post.upvotes} Power Votes</Typography>
                            </Box>
                                    </Box>
                                ))
                            )}
                            </Box>
                        )}
                        {selectedPost && (
                            <Box
                                mt={3}
                                p={2}
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    mx: "auto",
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    backgroundColor: "white"
                                }}
                            >
                                <Typography variant="h4">{selectedPost.title}</Typography>

                                <Typography variant="caption" color="text.secondary">
                                    {new Date(selectedPost.created_at).toLocaleString()}
                                </Typography>

                                {/*<Typography sx={{ whiteSpace: "pre-line", mt: 2 }}>
                                    {selectedPost.content}
                                </Typography>*/}
                                <Box
                                    sx={{ mt: 2 }}
                                    dangerouslySetInnerHTML={{
                                        __html: convertLexicalJsonToSanitizedHtml(selectedPost.content),
                                    }}
                                />

                                {/* Optional Close Button */}
                                <Button sx={{ mt: 2 }} onClick={() => setSelectedPost(null)}>
                                    Close
                                </Button>
                            </Box>
                        )}
                        <Dialog 
                            open={openBlogDialog} 
                            onClose={() => setOpenBlogDialog(false)} 
                            maxWidth="md" 
                            fullWidth
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: "#FFFDF8",
                                        padding: 2,
                                        minHeight: "800px",   // makes dialog taller
                                        width: "100%",
                                    }
                                }
                            }}
                        >
                            <DialogTitle>Create Blog Post</DialogTitle>
                            <DialogContent>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={blogTitle}
                                    onChange={(e) => setBlogTitle(e.target.value)}
                                    //sx={{ mt: 1 }}
                                    sx={{ mt: 1, mb: 3 }}
                                />
                                {/*}
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={23}
                                    //label="Content"
                                    placeholder="Let yourself muse, and write with intellectual depth and clarity..."
                                    value={blogContent}
                                    onChange={(e) => setBlogContent(e.target.value)}
                                    sx={{ mt: 2 }}
                                />*/}
                                <LexicalBlogEditor
                                    value={blogContent}
                                    onChange={(json) => setBlogContent(json)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenBlogDialog(false)}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    onClick={async () => {
                                        await axios.post("/blog/create", {
                                            title: blogTitle,
                                            content: blogContent
                                        });

                                        // Refresh posts
                                        const res = await axios.get(`/blog/user/${user._id}`);
                                        setBlogPosts(res.data);

                                        setOpenBlogDialog(false);
                                        setBlogTitle("");
                                        setBlogContent("");
                                    }}
                                >
                                    Post
                                </Button>
                            </DialogActions>
                        </Dialog>

                        </Box>
                    )}
                    {selectedTab === "Book Reviews" && (
                        <Box sx={{ 
                                textAlign: "left", 
                                }}
                        >
                            <Box display="flex" gap={2} mb={2}>
                                <Button
                                    variant={sortBookReviewsType === "latest" ? "contained" : "outlined"}
                                    onClick={() => setSortBookReviewsType("latest")}
                                >
                                    Latest
                                </Button>

                                <Button
                                    variant={sortBookReviewsType === "top" ? "contained" : "outlined"}
                                    onClick={() => setSortBookReviewsType("top")}
                                >
                                    Top
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => setOpenBookReviewsDialog(true)}
                                >
                                    Post
                                </Button>
                            </Box>
                        {!selectedBookReview && (
                            <Box>
                            {bookReviews.length === 0 ? (
                                <Typography variant="body1">No blog posts yet.</Typography>
                            ) : (
                                sortedBookReviews.map((post) => (
                                    <Box
                                        key={post._id}
                                        onClick={() => setSelectedBookReview(post)}
                                        p={2}
                                        sx={{
                                            border: "1px solid #ccc",
                                            borderRadius: 2,
                                            mb: 2,
                                            position: "relative",
                                            maxWidth: 900,
                                            mx: "auto",
                                            cursor: "pointer",
                                            "&:hover": { backgroundColor: "#f9f9f9" },
                                        }}
                                    >
                                        <Typography variant="h6">{post.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(post.created_at).toLocaleString()}
                                        </Typography>

                                        {/*<Box mt={2} display="flex" alignItems="center" gap={1}>
                                            <Button
                                                size="small"
                                                variant={post.voted ? "contained" : "outlined"}
                                                onClick={async (e) => {
                                                e.stopPropagation(); // prevents clicking the post
                                                toggleBookReviewsVote(post._id);
                                            }}
                                            >
                                                ▲ {post.voted ? "Unvote" : "Upvote"}
                                            </Button>
                                        <Typography variant="body2">{post.upvotes} upvotes</Typography>
                                    </Box>*/}
                                    <Box mt={2} display="flex" alignItems="center" gap={1}>
                                    {post.voted ? (
                                        <Box
                                        sx={{
                                            position: "relative",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            "&:hover .voted-btn": {
                                                opacity: 0,
                                            },
                                            "&:hover .undo-btn": {
                                                opacity: 1,
                                                pointerEvents: "auto",
                                            },
                                        }}
                                    >
                                            <Button
                                                size="small"
                                                className="voted-btn"
                                                variant="contained"
                                                color="success"
                                                disabled
                                                sx={{
                                                transition: "opacity 0.2s ease",
                                                }}
                                            >
                                            Power Voted
                                        </Button>
                                        <Button
                                                size="small"
                                                className="undo-btn"
                                                variant="contained"
                                                color="error"
                                                sx={{
                                                inset: 0,
                                                position: "absolute",
                                                transition: "opacity 0.2s ease",
                                                opacity: 0,
                                                pointerEvents: "none",
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleBookReviewsVote(post._id, post.type);
                                            }}
                                        >
                                                Undo
                                        </Button>
                                    </Box>
                                    ) : (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={(e) => {
                                            e.stopPropagation();
                                            toggleBookReviewsVote(post._id, post.type);}
                                        }
                                        >
                                            ▲ Power Vote
                                        </Button>
                                    )}
                                    <Typography variant="body2">{post.upvotes} Power Votes</Typography>
                            </Box>
                                    </Box>
                                ))
                            )}
                            </Box>
                        )}
                        {selectedBookReview && (
                            <Box
                                mt={3}
                                p={2}
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    mx: "auto",
                                    border: "1px solid #ddd",
                                    borderRadius: "8px",
                                    backgroundColor: "white"
                                }}
                            >
                                <Typography variant="h4">{selectedBookReview.title}</Typography>

                                <Typography variant="caption" color="text.secondary">
                                    {new Date(selectedBookReview.created_at).toLocaleString()}
                                </Typography>

                                <Box
                                    sx={{ mt: 2 }}
                                    dangerouslySetInnerHTML={{
                                        __html: convertLexicalJsonToSanitizedHtml(selectedBookReview.content),
                                    }}
                                />

                                <Button sx={{ mt: 2 }} onClick={() => setSelectedBookReview(null)}>
                                    Close
                                </Button>
                            </Box>
                        )}
                        <Dialog 
                            open={openBookReviewsDialog} 
                            onClose={() => setOpenBookReviewsDialog(false)} 
                            maxWidth="md" 
                            fullWidth
                            slotProps={{
                                paper: {
                                    sx: {
                                        backgroundColor: "#FFFDF8",
                                        padding: 2,
                                        minHeight: "800px",   // makes dialog taller
                                        width: "100%",
                                    }
                                }
                            }}
                        >
                            <DialogTitle>Create Blog Post</DialogTitle>
                            <DialogContent>
                                <TextField
                                    fullWidth
                                    label="Title"
                                    value={bookReviewTitle}
                                    onChange={(e) => setBookReviewTitle(e.target.value)}
                                    sx={{ mt: 1, mb: 3 }}
                                />
                                <LexicalBlogEditor
                                    value={bookReviewContent}
                                    onChange={(json) => setBookReviewContent(json)}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setOpenBookReviewsDialog(false)}>Cancel</Button>
                                <Button
                                    variant="contained"
                                    onClick={async () => {
                                        await axios.post("/bookReview/create", {
                                            title: bookReviewTitle,
                                            content: bookReviewContent
                                        });

                                        // Refresh posts
                                        const res = await axios.get(`/bookReviews/user/${user._id}`);
                                        setBookReviews(res.data);

                                        setOpenBookReviewsDialog(false);
                                        setBookReviewTitle("");
                                        setBookReviewContent("");
                                    }}
                                >
                                    Post
                                </Button>
                            </DialogActions>
                        </Dialog>

                        </Box>
                    )}
                </Box>
            </Box>
            </Box>

        </Box>
        {/*
        <Box display="flex" justifyContent="center" mt={5} px={2}>
            <Card sx={{ width: '100%', maxWidth: 800, boxShadow: 3 }}>
                <Box sx={{ position: 'relative', height: 200 }}>
                <img
                    src={userInfo?.bannerUrl || '/default-banner.jpg'}
                    alt="Banner"
                    style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderTopLeftRadius: 4,
                    borderTopRightRadius: 4,
                    }}
                />
                <Avatar
                    src={userInfo?.profileImageUrl || '/default-avatar.jpg'}
                    alt="Profile"
                    sx={{
                    width: 100,
                    height: 100,
                    position: 'absolute',
                    bottom: -50,
                    left: 24,
                    border: '3px solid white',
                    }}
                />
                </Box>
                <CardContent sx={{ pt: 6 }}>
                <Typography variant="h6">{userInfo?.first_name} {userInfo?.last_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {userInfo?.occupation || 'Your occupation'}
                </Typography>
                </CardContent>
            </Card>
        </Box>
        */}
        {/* contain content on page */}
        {/*}
        <Container 
            // limit container width to a small size
            maxWidth="sm"
            // vertically center and align text
            style={{ marginTop: "20vh", textAlign: "center" }}
            >
                */}
                {/*
        <Dialog
            open={openEditProfileDialog}
            onClose={() => setOpenEditProfileDialog(false)}
            maxWidth="sm"
            fullWidth
            >
                <Typography variant="h4">{username}</Typography>

                {userInfo && (
                    <Box mt={2} textAlign="left">
                        <Typography variant="h6">Your Profile Info</Typography>
                        {[
                        { label: "Username",      field: "username"     },
                        { label: "First name",    field: "first_name"   },
                        { label: "Last name",     field: "last_name"    },
                        { label: "Occupation",    field: "occupation"   },
                        { label: "Company",       field: "company"      },
                        { label: "University",    field: "university"   },
                        { label: "Blurb",         field: "blurb"        },
                        ].map(({ label, field }) => (
                        <Box key={field} mb={1}>
                            <Typography variant="subtitle2">{label}:</Typography>

                            {isEditing ? (
                            <input
                                type="text"
                                name={field}
                                value={editInfo[field]}
                                onChange={handleChange}
                                style={{ width: "100%", padding: "4px" }}
                            />
                            ) : (
                            <Typography>
                                {userInfo[field] || "N/A"}
                            </Typography>
                            )}
                        </Box>
                        ))}

                        <Box mt={2}>
                        {isEditing ? (
                            <>
                            <Button variant="contained" color="primary" onClick={saveChanges}>
                                Save
                            </Button>
                            <Button
                                variant="outlined"
                                color="secondary"
                                onClick={() => setIsEditing(false)}
                                sx={{ ml: 2 }}
                            >
                                Cancel
                            </Button>
                            </>
                        ) : (
                            <Button variant="outlined" onClick={() => setIsEditing(true)}>
                            Edit Profile
                            </Button>
                        )}
                        </Box>
                    </Box>
                )}
        </Dialog>
        */}
        <Dialog
            open={openEditProfileDialog}
            onClose={() => setOpenEditProfileDialog(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                borderRadius: 3,
                p: 1.5,
                backgroundColor: "#FFFDF8"
                }
            }}
            >
            <DialogTitle
                sx={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.8rem",
                pb: 0
                }}
            >
                Edit Profile
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
                {userInfo && (
                <Box>

                    <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", mb: 1 }}
                    >
                    Your Profile Info
                    </Typography>

                    <Divider sx={{ mb: 2 }} />

                    {/* Loop through fields */}
                    {[
                    { label: "Username", field: "username" },
                    { label: "First name", field: "first_name" },
                    { label: "Last name", field: "last_name" },
                    { label: "Occupation", field: "occupation" },
                    { label: "University", field: "university" },
                    ].map(({ label, field, multiline }) => (
                    <Box key={field} mb={2}>
                        {isEditing ? (
                        <TextField
                            fullWidth
                            label={label}
                            name={field}
                            value={editInfo[field] ?? ""}
                            multiline={multiline}
                            minRows={multiline ? 3 : 1}
                            onChange={handleChange}
                        />
                        ) : (
                        <>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                            {label}
                            </Typography>
                            <Typography variant="body1" sx={{ pl: 0.5 }}>
                            {userInfo[field] || "N/A"}
                            </Typography>
                        </>
                        )}
                    </Box>
                    ))}
                </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ pb: 2, pr: 2 }}>
                {isEditing ? (
                <>
                    <Button variant="contained" onClick={saveChanges}>
                    Save
                    </Button>
                    <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => setIsEditing(false)}
                    >
                    Cancel
                    </Button>
                </>
                ) : (
                <Button variant="outlined" onClick={() => setIsEditing(true)}>
                    Edit Profile
                </Button>
                )}
            </DialogActions>
            </Dialog>

        {/*</></Container>*/}
    </>
    </div>
    );
}

export default ProfilePage;